import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Container, Card, Button, Spinner, Alert, Badge, Modal, Form, Nav, Dropdown } from 'react-bootstrap';
import { Tooltip } from 'react-tooltip';
import { useTranslation } from 'react-i18next';
import { isRTL } from '../i18n/config';
import ScrollableTabs from '../components/ScrollableTabs';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useDebug } from '../contexts/DebugContext';
import { useSaving } from '../contexts/SavingContext';
import { ApiActions } from '../shared/apiActions';
import apiCall from '../shared/apiCall';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import { TOURS } from '../shared/tourSteps';
import { isAdmin as checkIsAdmin, getCachedAccountData, updateAccountUsage } from '../shared/accountCache';
import { canUserDo } from '../shared/tierPermissions';
import { safeFormatDate, calculateActivityDateTime, addDaysToString, tryParseJson, getHigherTier, formatDateWithLocale, getCurrentDayInPlan } from '../shared/utils';
import { createActivityByType, createNormalizedActivity, sortActivitiesByTime, isDayWithinAccommodationRange, findMultiDayAccommodations, activityMatchesOption } from '../shared/activityUtils';
import { buildTransportationSegments } from '../shared/transportationSegments';
import LocationsTab from '../components/LocationsTab';
import CostsTab from '../components/CostsTab';
import OverviewTab from '../components/OverviewTab';
import WeatherTab from '../components/WeatherTab';
import InfoTab from '../components/InfoTab';
import MainLocationsTab from '../components/MainLocationsTab';
import TransportationInfoTab from '../components/TransportationInfoTab';
import { renderActivity } from '../components/activities';
import AccommodationModal from '../components/activities/AccommodationModal';
import TransportationModal from '../components/activities/TransportationModal';
import AttractionModal from '../components/activities/AttractionModal';
import { DEFAULT_CURRENCY } from '../shared/constants';
import { createTripTemplatesV2 } from '../shared/createTripTemplatesV2';
import { normalizeLocation } from '../shared/utils';
import TodoList from '../components/TodoList';
import PackingTab from '../components/PackingTab';
import SlidingMenu from '../components/SlidingMenu';
import { generateUUID } from '../shared/utils';
import { fetchAttractionImages } from '../shared/utils';
import { clog, cerror } from '../shared/consoleLogger';
// Add import for SuggestedAttractionsModal
import SuggestedAttractionsModal from '../components/SuggestedAttractionsModal';
import DayMapModal from '../components/DayMapModal';
import VersionFooter from '../components/VersionFooter';
import MinimizedDaysViewSimple from '../components/MinimizedDaysViewSimple';
import ShareOptionsModal from '../components/ShareOptionsModal';
import ShareToGroupModal from '../components/ShareToGroupModal';
import AlertModal from '../components/AlertModal';
import SwapDaysModal from '../components/SwapDaysModal';
import DaySummaryModal from '../components/DaySummaryModal';
import DayShortsMapModal from '../components/DayShortsMapModal';
import DayShortsStatsModal from '../components/DayShortsStatsModal';

// Use V2 templates
const templates = createTripTemplatesV2;

// Helper function to check if a day has multiple itinerary options
const hasMultipleOptions = (day) => {
  if (!day || !day.activities || day.activities.length === 0) return false;
  
  const activityOptionSets = [];
  
  day.activities.forEach(activity => {
    const options = activity.option;
    let optionSet;
    
    if (!options || (Array.isArray(options) && options.length === 0)) {
      // Empty array or undefined/null means all options (1, 2, 3)
      optionSet = new Set([1, 2, 3]);
    } else if (options === 'all') {
      // "all" means all options (1, 2, 3)
      optionSet = new Set([1, 2, 3]);
    } else if (Array.isArray(options)) {
      optionSet = new Set(options);
    } else if (typeof options === 'number') {
      optionSet = new Set([options]);
    } else {
      optionSet = new Set([1, 2, 3]); // Default to all
    }
    
    activityOptionSets.push(optionSet);
  });
  
  // Check if all activities have the same option set
  if (activityOptionSets.length === 0) return false;
  
  const firstSet = activityOptionSets[0];
  const allSame = activityOptionSets.every(set => {
    if (set.size !== firstSet.size) return false;
    return Array.from(set).every(opt => firstSet.has(opt));
  });
  
  // Return true if activities have different option sets (multiple options)
  return !allSame;
};

// Helper function to check if a day is in the past
const isDayInPast = (dayDate) => {
  if (!dayDate) return false;
  try {
    const dayDateObj = new Date(dayDate);
    const today = new Date();
    // Set both to midnight for accurate comparison
    today.setHours(0, 0, 0, 0);
    dayDateObj.setHours(0, 0, 0, 0);
    return dayDateObj < today;
  } catch (error) {
    cerror('Error checking if day is in past:', error);
    return false;
  }
};

// Helper function to generate Google Photos URL for a specific date
const getGooglePhotosUrl = (dateString) => {
  if (!dateString) return '';
  try {
    // Extract date in YYYY-MM-DD format
    const datePart = safeFormatDate(dateString, 'yyyy-MM-dd');
    if (!datePart || datePart === 'Invalid date') return '';
    // Google Photos search URL format
    return `https://photos.google.com/search/${datePart}`;
  } catch (error) {
    cerror('Error generating Google Photos URL:', error);
    return '';
  }
};

// Function to clean plan data for group sharing (with options for what to include)
const cleanPlanForGroupSharing = (planData, exclusions = {}, options = {}) => {
  // Deep clone to avoid mutating the original plan data
  const cleanedData = JSON.parse(JSON.stringify(planData));
  
  const {
    includeTodos = true,
    includeCosts = true,
    includeNotes = true,
    includePackingList = true,
    includeInfo = true
  } = options;
  
  // Remove or clear private data based on options
  if (!includeCosts) {
    cleanedData.additionalCosts = [];
  }
  if (!includeTodos) {
    cleanedData.todos = [];
  }
  if (!includePackingList) {
    cleanedData.packingItems = [];
  }
  if (!includeInfo) {
    cleanedData.infoSections = []; // Exclude info sections if not included
  }
  
  // Get excluded activity IDs (empty array = all included, non-empty = those IDs are excluded)
  const excludedIds = exclusions.excludedActivityIds || [];
  const excludedIdsStr = excludedIds.length > 0 ? excludedIds.map(id => String(id)) : [];
  
  // Clear cost values (set to empty) when not including costs — empty = don't show; 0 = show zero
  const cleanCosts = (obj) => {
    if (obj && typeof obj === 'object') {
      if (obj.cost && !includeCosts) {
        obj.cost = { ...obj.cost, value: '' };
      }
      // Recursively clean nested objects
      Object.keys(obj).forEach(key => {
        if (typeof obj[key] === 'object' && obj[key] !== null) {
          if (Array.isArray(obj[key])) {
            obj[key].forEach(item => cleanCosts(item));
          } else {
            cleanCosts(obj[key]);
          }
        }
      });
    }
  };
  
  // Clean day-specific todos, filter activities, and clean costs/notes
  if (cleanedData.days) {
    cleanedData.days = cleanedData.days.map(day => {
      let activities = day.activities || [];
      
      // Filter activities: if excludedIds is empty, include all; otherwise exclude those in the array
      if (excludedIdsStr.length > 0) {
        activities = activities.filter(activity => {
          // Only include activities that are NOT in the excluded list
          return activity.id && !excludedIdsStr.includes(String(activity.id));
        });
      }
      
      // Clean costs and notes from remaining activities
      activities = activities.map(activity => {
        const cleanedActivity = { ...activity };
        
        // Clear costs if not including costs (empty = don't show in UI)
        if (!includeCosts) {
          cleanCosts(cleanedActivity);
          if (cleanedActivity.cost) {
            cleanedActivity.cost = { ...cleanedActivity.cost, value: '' };
          }
        }
        
        // Filter out private links (keep only public links)
        if (cleanedActivity.links && Array.isArray(cleanedActivity.links)) {
          cleanedActivity.links = cleanedActivity.links.filter(link => link.isPrivate === false);
        }
        
        // Clear notes if not including notes
        if (!includeNotes) {
          cleanedActivity.notes = '';
        }
        
        return cleanedActivity;
      });
      
      return {
        ...day,
        todos: includeTodos ? (day.todos || []) : [],
        activities: activities
      };
    });
  }
  
  // Clean any additional cost fields if not including costs
  if (!includeCosts) {
    cleanCosts(cleanedData);
  }
  
  return cleanedData;
};


// Create a new activity with consistent defaults
const createNewActivity = (params = {}) => {
  return createActivityByType(params);
};

// Add at the top, after imports
function updateActivityDateTime(activity, newDayDate) {
  // newDayDate: 'YYYY-MM-DD' (from day.date)
  // activity.startDateTime: 'YYYY-MM-DDTHH:mm:ss.sssZ' or ''
  if (!activity.startDateTime) return activity;
  const timePart = activity.startDateTime.split('T')[1] || '12:00:00.000Z';
  const newStart = `${newDayDate}T${timePart}`;
  let newEnd = activity.endDateTime;
  if (activity.endDateTime) {
    const endTimePart = activity.endDateTime.split('T')[1] || '13:00:00.000Z';
    newEnd = `${newDayDate}T${endTimePart}`;
  }
  return { ...activity, startDateTime: newStart, endDateTime: newEnd };
}

export default function PlanDetail() {
  const { t, i18n } = useTranslation();
  const { planId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [plan, setPlan] = useState(null);
  const [planVersion, setPlanVersion] = useState(1);
  const [parsedData, setParsedData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [alertVariant, setAlertVariant] = useState('error'); // 'error' | 'warning' | 'info' | 'success'
  const [successMessage, setSuccessMessage] = useState('');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState('');
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'days');  
  
  // Guided tour setup
  const overviewDriverObj = useRef(null);
  const daysDriverObj = useRef(null);
  const tourCheckInProgress = useRef(false); // Prevent multiple concurrent checks
  
  const startOverviewTour = useCallback(() => {
    // Prevent multiple starts
    if (overviewDriverObj.current) return;
    
    // Check localStorage first - only update if not already set!
    const alreadySeen = localStorage.getItem('guidedTourOverviewTabSeen') === 'true';
    if (!alreadySeen) {
      // Update account.usage async (fire and forget - non-blocking!)
      updateAccountUsage({ guidedTourOverviewTabSeen: true })
        .catch(err => console.error('Error updating tour status:', err));
      
      // Set localStorage AFTER updating account (only update once!)
      localStorage.setItem('guidedTourOverviewTabSeen', 'true');
    }
    
    // Translate tour steps
    const tourSteps = TOURS.OVERVIEW_TAB.map(step => ({
      ...step,
      popover: {
        ...step.popover,
        title: step.translationKey ? t(`planDetail.tour.${step.translationKey}.title`) : step.popover.title,
        description: step.translationKey ? t(`planDetail.tour.${step.translationKey}.description`) : step.popover.description
      }
    }));
    
    overviewDriverObj.current = driver({
      showProgress: true,
      showButtons: ['next', 'previous', 'close'],
      allowClose: true,
      popoverClass: 'driverjs-theme',
      nextBtnText: t('planDetail.tour.next'),
      prevBtnText: t('planDetail.tour.previous'),
      doneBtnText: t('planDetail.tour.close'),
      steps: tourSteps,
      onDestroyed: () => {
        overviewDriverObj.current = null;
      }
    });
    overviewDriverObj.current.drive();
  }, [t]);
  
  const startDaysTour = useCallback(() => {
    // Prevent multiple starts
    if (daysDriverObj.current) return;
    
    // Check localStorage first - only update if not already set!
    const alreadySeen = localStorage.getItem('guidedTourDaysTabSeen') === 'true';
    if (!alreadySeen) {
      // Update account.usage async (fire and forget - non-blocking!)
      updateAccountUsage({ guidedTourDaysTabSeen: true })
        .catch(err => console.error('Error updating tour status:', err));
      
      // Set localStorage AFTER updating account (only update once!)
      localStorage.setItem('guidedTourDaysTabSeen', 'true');
    }
    
    // Dynamically determine which actions element to use based on screen size
    const actionsElement = window.innerWidth >= 1200 
      ? '#desktop-actions-buttons' 
      : '#plan-actions-dropdown';
    
    // Create tour steps with the correct selector and translations
    const tourSteps = TOURS.DAYS_TAB.map(step => {
      const translatedStep = {
        ...step,
        popover: {
          ...step.popover,
          title: step.translationKey ? t(`planDetail.tour.${step.translationKey}.title`) : step.popover.title,
          description: step.translationKey ? t(`planDetail.tour.${step.translationKey}.description`) : step.popover.description
        }
      };
      
      if ((step.element && step.element.includes('plan-actions-dropdown')) || step.element?.includes('desktop-actions-buttons')) {
        return {
          ...translatedStep,
          element: actionsElement
        };
      }
      return translatedStep;
    });
    
    daysDriverObj.current = driver({
      showProgress: true,
      showButtons: ['next', 'previous', 'close'],
      allowClose: true,
      popoverClass: 'driverjs-theme',
      nextBtnText: t('planDetail.tour.next'),
      prevBtnText: t('planDetail.tour.previous'),
      doneBtnText: t('planDetail.tour.close'),
      steps: tourSteps,
      onDestroyed: () => {
        daysDriverObj.current = null;
      }
    });
    daysDriverObj.current.drive();
  }, [t]);
  
  // Activity modal state
  const [showModal, setShowModal] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [selectedDayId, setSelectedDayId] = useState(null);
  const [isNewActivity, setIsNewActivity] = useState(false);
  const [currentActivityType, setCurrentActivityType] = useState(null);
  
  // Suggested activities state
  const [suggestedAttractions, setSuggestedAttractions] = useState([]);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [showSuggestionsModal, setShowSuggestionsModal] = useState(false);
  
  // Delete confirmation modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [activityToDelete, setActivityToDelete] = useState(null);
  const [dayIdOfActivityToDelete, setDayIdOfActivityToDelete] = useState(null);
  
  // Add new state for AI Activity modal
  const [showAiModal, setShowAiModal] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [isGeneratingActivity, setIsGeneratingActivity] = useState(false);
  const [generatedActivity, setGeneratedActivity] = useState(null);
  const [generatedActivities, setGeneratedActivities] = useState([]);
  const [selectedActivityIndex, setSelectedActivityIndex] = useState(0);
  const [aiError, setAiError] = useState('');
  const [useTripContext, setUseTripContext] = useState(true);
  
  // Add a new state for the activity type
  const [aiActivityType, setAiActivityType] = useState('attraction');
  
  // Admin status state
  const [isAdmin, setIsAdmin] = useState(false); 
  
  // Account settings state
  const [accountSettings, setAccountSettings] = useState({ useGooglePhotos: false });
  
  const [canShare, setCanShare] = useState(false);
  const [canExportPDF, setCanExportPDF] = useState(false);
  const [canManageGroups, setCanManageGroups] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  
  // Share functionality state
  const [showShareOptionsModal, setShowShareOptionsModal] = useState(false);
  const [shareExclusions, setShareExclusions] = useState({
    excludedActivityIds: [] // Array of activity IDs to exclude (empty = all included by default)
  });
  const [expandedSections, setExpandedSections] = useState({
    transportation: false,
    accommodation: false
  });
  
  // Share to Groups functionality state
  const [showShareToGroupModal, setShowShareToGroupModal] = useState(false);
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);
  const [isSharingToGroups, setIsSharingToGroups] = useState(false);
  const [shareToGroupExclusions, setShareToGroupExclusions] = useState(null);
  const [shareToGroupExpandedSections, setShareToGroupExpandedSections] = useState({
    transportation: false,
    accommodation: false
  });
  const [shareToGroupOptions, setShareToGroupOptions] = useState(null);
  const [shareToGroupEmailNotification, setShareToGroupEmailNotification] = useState('new'); // 'new', 'all', or 'none'
  const [shareToGroupMessage, setShareToGroupMessage] = useState({ type: null, text: null });
  
  // Day map modal state
  const [showDayMapModal, setShowDayMapModal] = useState(false);
  const [selectedDayForMap, setSelectedDayForMap] = useState(null);
  const [selectedDayIndexForMap, setSelectedDayIndexForMap] = useState(null);
  const [continuingAccommodationsForMap, setContinuingAccommodationsForMap] = useState([]);
  // YouTube Shorts map modal state
  const [showDayShortsMapModal, setShowDayShortsMapModal] = useState(false);
  const [selectedDayForShortsMap, setSelectedDayForShortsMap] = useState(null);
  const [selectedDayIndexForShortsMap, setSelectedDayIndexForShortsMap] = useState(null);
  const [showDayShortsStatsModal, setShowDayShortsStatsModal] = useState(false);
  const [selectedDayForShortsStats, setSelectedDayForShortsStats] = useState(null);
  const [selectedDayIndexForShortsStats, setSelectedDayIndexForShortsStats] = useState(null);
  // Swap days modal state
  const [showSwapDaysModal, setShowSwapDaysModal] = useState(false);
  const [swapDaysSourceDayId, setSwapDaysSourceDayId] = useState(null);
  // Day summary modal state
  const [showDaySummaryModal, setShowDaySummaryModal] = useState(false);
  const [daySummaryDayId, setDaySummaryDayId] = useState(null);
  const [showGeminiNavigateModal, setShowGeminiNavigateModal] = useState(false);
  // Option filter state: key is day.id, value is 'all' | 1 | 2 | 3 (default is 1)
  const [dayOptionFilters, setDayOptionFilters] = useState({});
  
  // State to track which days have all activities collapsed
  const [collapsedDays, setCollapsedDays] = useState({});
  
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { debugMode } = useDebug();
  const { setIsSaving } = useSaving();
  
  // Check if user is admin (using cached data)
  useEffect(() => {
    if (!currentUser) {
      setIsAdmin(false);
      return;
    }
    
    const checkAdmin = async () => {
      try {
        const adminStatus = await checkIsAdmin();
        setIsAdmin(adminStatus);
      } catch (error) {
        cerror('Error checking admin status:', error);
        setIsAdmin(false);
      }
    };
    
    checkAdmin();
  }, [currentUser]);

  // Fetch account settings
  useEffect(() => {
    if (!currentUser) {
      setAccountSettings({ useGooglePhotos: false });
      return;
    }
    
    const fetchAccountSettings = async () => {
      try {
        const accountData = await getCachedAccountData();
        if (accountData && accountData.settings) {
          setAccountSettings(accountData.settings);
        }
      } catch (error) {
        cerror('Error fetching account settings:', error);
        setAccountSettings({ useGooglePhotos: false });
      }
    };
    
    fetchAccountSettings();
  }, [currentUser]);
  
  // Check plan access tier and permissions (using higher of plan.access or account tier)
  useEffect(() => {
    if (!currentUser || !plan) {      
      setCanShare(false);
      setCanExportPDF(false);
      return;
    }
    
    const checkPermissions = async () => {
      try {
        // Get account tier
        const accountData = await getCachedAccountData();
        const accountTier = accountData?.subscription?.tier || 'free';
        
        // Use the higher tier between plan.access and account.subscription.tier
        const planAccess = getHigherTier(plan.access, accountTier);
        
        const sharePermission = canUserDo(planAccess, 'canShare');
        const pdfPermission = canUserDo(planAccess, 'canExportPDF');
        const groupsPermission = canUserDo(planAccess, 'canManageGroups');        
        
        setCanShare(sharePermission);
        setCanExportPDF(pdfPermission);
        setCanManageGroups(groupsPermission);
      } catch (error) {
        cerror('Error checking permissions:', error);
        setCanShare(false);
        setCanExportPDF(false);
        setCanManageGroups(false);
      }
    };
    
    checkPermissions();
  }, [currentUser, plan]);

  // Load groups from account cache (groups are per account, not per plan)
  // Add new state for managing todos    
  const [dayTodos, setDayTodos] = useState({});
  
  // Add new state to track expanded day todo sections
  const [expandedTodoSections, setExpandedTodoSections] = useState({});
  
  // State for minimized view
  const [isMinimized, setIsMinimized] = useState(false);
  
  // Function to toggle the expanded state for a specific day's todo section
  const toggleTodoSection = (dayId) => {
    setExpandedTodoSections(prev => ({
      ...prev,
      [dayId]: !prev[dayId]
    }));
  };

  // Check if a day's todo section should be expanded
  const isTodoSectionExpanded = (dayId) => {
    // If there are todos, check the expandedTodoSections state
    // If no todos exist, default to collapsed
    const hasTodos = dayTodos[dayId] && dayTodos[dayId].length > 0;
    return (hasTodos && expandedTodoSections[dayId] !== false) || 
       (!hasTodos && expandedTodoSections[dayId] === true);
  };

  const isDaySummarySectionExpanded = (day) => day?.summaryCollapsed !== true;

  const toggleDaySummarySection = async (dayId) => {
    if (!parsedData?.days) return;
    const day = parsedData.days.find((d) => d.id === dayId);
    if (!day) return;

    const updatedData = {
      ...parsedData,
      days: parsedData.days.map((d) => (
        d.id === dayId ? { ...d, summaryCollapsed: d.summaryCollapsed !== true } : d
      ))
    };
    await saveChanges(updatedData);
  };

  // Load plan details when component mounts or planId changes
  // Note: Authentication is handled by ProtectedRoute wrapper, so currentUser is guaranteed to exist
  useEffect(() => {
    if (!planId) {
      setError('No plan ID provided');
      setIsLoading(false);
      return;
    }
    
    // Reset states when loading a new plan
    setParsedData(null);
    setPlan(null);
    setPlanVersion(1); // Reset version
    setSuggestedAttractions([]);
    
    fetchPlanDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planId]);
  
  // Extract todos from parsedData whenever it changes
  useEffect(() => {
    // Create empty defaults
    const updatedPlanTodos = [];
    const extractedDayTodos = {};
    const updatedPackingItems = [];

    // Only process if we have valid data
    if (parsedData) {
      // Extract plan-level todos if they exist
      if (Array.isArray(parsedData.todos)) {
        updatedPlanTodos.push(...parsedData.todos);
      }
      
      // Extract day-specific todos if they exist
      if (Array.isArray(parsedData.days)) {
        parsedData.days.forEach(day => {
          if (day && day.id) {
            extractedDayTodos[day.id] = Array.isArray(day.todos) ? [...day.todos] : [];
          }
        });
      }
      
      // Extract packing items if they exist
      if (Array.isArray(parsedData.packingItems)) {
        updatedPackingItems.push(...parsedData.packingItems);
      }
    }
    
    // Always set state (not conditionally)    
    setDayTodos(extractedDayTodos);    
  }, [parsedData]);
  
  
  // Check and start tours when data is ready (once per account, not per plan!)
  useEffect(() => {
    if (!parsedData || tourCheckInProgress.current) return;
    
    // Mark as in progress immediately to prevent race conditions
    tourCheckInProgress.current = true;
    
    const tab = searchParams.get('tab') || 'days';
    if (tab !== 'days' && tab !== 'overview') {
      tourCheckInProgress.current = false;
      return;
    }
    
    // Check localStorage first
    const overviewSeen = localStorage.getItem('guidedTourOverviewTabSeen') === 'true';
    const daysSeen = localStorage.getItem('guidedTourDaysTabSeen') === 'true';
    
    // If both seen, done
    if (overviewSeen && daysSeen) {
      tourCheckInProgress.current = false;
      return;
    }
    
    // Check specific tab
    if (tab === 'overview' && overviewSeen) {
      tourCheckInProgress.current = false;
      return;
    }
    if (tab === 'days' && daysSeen) {
      tourCheckInProgress.current = false;
      return;
    }
    
    // Not seen - check account.usage and start tour
    (async () => {
      try {
        const accountData = await getCachedAccountData();
        
        if (tab === 'overview' && !overviewSeen) {
          const accountSeen = accountData?.usage?.guidedTourOverviewTabSeen === true;
          if (!accountSeen) {
            setTimeout(() => startOverviewTour(), 1500);
          } else {
            localStorage.setItem('guidedTourOverviewTabSeen', 'true');
          }
        }
        
        if (tab === 'days' && !daysSeen && parsedData.days?.length > 0) {
          const accountSeen = accountData?.usage?.guidedTourDaysTabSeen === true;
          if (!accountSeen) {
            setTimeout(() => startDaysTour(), 800);
          } else {
            localStorage.setItem('guidedTourDaysTabSeen', 'true');
          }
        }
      } catch (error) {
        cerror('Error checking tour status:', error);
      } finally {
        tourCheckInProgress.current = false;
      }
    })();
  }, [parsedData, searchParams, startOverviewTour, startDaysTour]);

  // Function to move an activity from one day to another
  const moveActivityBetweenDays = async (activity, fromDayId, toDayId, targetDayDate) => {
    try {
      // Create a copy of the parsed data
      const updatedData = { ...parsedData };
      
      // Find day indices
      const fromDayIndex = updatedData.days.findIndex(day => day.id === fromDayId);
      const toDayIndex = updatedData.days.findIndex(day => day.id === toDayId);
      
      if (fromDayIndex === -1 || toDayIndex === -1) {
        throw new Error('Day not found');
      }
      
      // Remove from source day
      updatedData.days[fromDayIndex].activities = updatedData.days[fromDayIndex].activities.filter(
        a => a.id !== activity.id
      );
      
      // Update activity dates to match target day
      const updatedActivity = updateActivityDateTime(activity, targetDayDate);
      
      // Add to target day
      updatedData.days[toDayIndex].activities.push(updatedActivity);
      
      // Sort activities in target day by time
      updatedData.days[toDayIndex].activities = sortActivitiesByTime(updatedData.days[toDayIndex].activities);
      
      // Save changes
      const saved = await saveChanges(updatedData);
      
      if (saved) {
        return true;
      } else {
        // Error is already displayed by saveChanges via setError
        return false;
      }
    } catch (error) {
      cerror('Error moving activity between days:', error);
      // This should not happen now since saveChanges doesn't throw, but keep as safety net
      if (!error.message || !error.message.includes('VERSION_MISMATCH')) {
        setError('Failed to move activity. Please try again.');
      }
      return false;
    }
  };


  const fetchPlanDetails = async () => {
    setIsLoading(true);
    setError('');
    
    try {
      // Get plan details from API
      const response = await apiCall(`/api/model?action=${ApiActions.GET_PLAN_BY_ID}&planId=${planId}`);
      
      if (response.success && response.plan) {
        setPlan(response.plan);
        // Set plan version (default to 1 if not present for backward compatibility)
        setPlanVersion(response.plan.version || 1);
        
        // Parse the plan data
        try {
          const parsedData = JSON.parse(response.plan.data);
          
          // Initialize settings if they don't exist
          if (!parsedData.settings) {
            parsedData.settings = {};
          }
          
          // Add the new collapseCompletedActivities setting with default value if it doesn't exist
          if (parsedData.settings.collapseCompletedActivities === undefined) {
            parsedData.settings.collapseCompletedActivities = true;
          }
          
          // Process the data to ensure all required properties exist
          if (parsedData.days) {
            parsedData.days.forEach(day => {
              if (day.activities) {
                day.activities.forEach(activity => {
                  // Ensure the completed property is properly initialized
                  if (activity.completed === undefined) {
                    activity.completed = false;
                  } else {
                    // Convert string 'true'/'false' to boolean if needed
                    if (typeof activity.completed === 'string') {
                      activity.completed = activity.completed === 'true';
                    }
                  }
                });
              }
            });
          }
          
          // Set the parsed data
          setParsedData(parsedData);          
          
          // Process accommodations and transportations
          await processAccommodations(parsedData);
          await processTransportations(parsedData);
          
        } catch (parseError) {
          cerror('Error parsing plan data:', parseError);
          setError('Error parsing plan data. Please try again.');
        }
      } else {
        setError('Failed to load trip details. Please try again.');
      }
    } catch (error) {
      cerror('Error fetching plan details:', error);
      setError('An error occurred while loading the trip. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Function to process accommodations from AI JSON
  const processAccommodations = async (data) => {    
    try {
      if (!data || !data.accommodations || !Array.isArray(data.accommodations) || data.accommodations.length === 0) {
        clog("No accommodations to process");
        // Also check for "Accommodations" with capital A, as specified in the template
        if (!data.Accommodations || !Array.isArray(data.Accommodations) || data.Accommodations.length === 0) {
          return;
        }
        // Use the capital-A Accommodations array
        data = { accommodations: data.Accommodations };
      }
      
      const updatedData = { ...parsedData };
      let needsUpdate = false;
      
      // Add accommodation entries to the appropriate days
      data.accommodations.forEach(accommodation => {
        // Find the matching day for this accommodation's start date (use string date to avoid TZ shifts)
        const startDateStr = (accommodation.startDate || accommodation.startDateTime || '').split('T')[0];
        const matchingDayIndex = updatedData.days.findIndex(day => {
          const dayDateStr = (day.date || '').split('T')[0];
          return dayDateStr === startDateStr;
        });
        
        if (matchingDayIndex >= 0) {
          // Check if accommodation already exists
          const isAlreadyAdded = updatedData.days[matchingDayIndex].activities.some(
            activity => activity.type === 'accommodation' && activity.name === accommodation.name
          );
          
          if (!isAlreadyAdded) {
            // Create description with check-in/out times
            const description = (accommodation.description || '') + 
              `\n\nCheck-in: ${accommodation.checkInTime || "14:00"}, Check-out: ${accommodation.checkOutTime || "11:00"}`;
            
            // Create links array if website is available
            const links = accommodation.website ? 
              [{ name: "Website", url: accommodation.website }] : 
              [];
            
            // Create new activity for this accommodation using the utility function
            const newActivity = createNewActivity({
              type: 'accommodation',
              name: accommodation.name,
              description: description,
              location: accommodation.location || '',
              startDateTime: accommodation.startDate,
              endDateTime: accommodation.endDate,
              checkInTime: accommodation.checkInTime || "14:00",
              checkOutTime: accommodation.checkOutTime || "11:00",
              links: links,
              defaultCurrency: parsedData?.currency || DEFAULT_CURRENCY
            });
            
            // Add to the matching day
            updatedData.days[matchingDayIndex].activities.push(newActivity);
            needsUpdate = true;
          }
        }
      });
      
      // Save changes if needed
      if (needsUpdate) {
        await saveChanges(updatedData);
      }
    } catch (error) {
      cerror("Error processing accommodations:", error);
    } finally {      
    }
  };

  // Function to process transportations from AI JSON
  const processTransportations = async (data) => {    
    try {
      if (!data || !data.transportations || !Array.isArray(data.transportations) || data.transportations.length === 0) {
        clog("No transportations to process");
        // Also check for "Transportations" with capital T, as specified in the template
        if (!data.Transportations || !Array.isArray(data.Transportations) || data.Transportations.length === 0) {
          return;
        }
        // Use the capital-T Transportations array
        data = { transportations: data.Transportations };
      }
      
      const updatedData = { ...parsedData };
      let needsUpdate = false;
      
      // Add transportation entries to the appropriate days
      data.transportations.forEach(transportation => {
        // Find the matching day for this transportation's start date (use string date to avoid TZ shifts)
        const startDateStr = (transportation.startDate || transportation.startDateTime || '').split('T')[0];
        const matchingDayIndex = updatedData.days.findIndex(day => {
          const dayDateStr = (day.date || '').split('T')[0];
          return dayDateStr === startDateStr;
        });
        
        if (matchingDayIndex >= 0) {
          // Check if transportation already exists
          const isAlreadyAdded = updatedData.days[matchingDayIndex].activities.some(
            activity => activity.type === 'transportation' && activity.name === transportation.name
          );
          
          if (!isAlreadyAdded) {
            // Create links array if any links are available
            const links = [];
            if (transportation.links && Array.isArray(transportation.links)) {
              transportation.links.forEach(link => {
                if (link.url && link.name) {
                  links.push({
                    name: link.name,
                    url: link.url
                  });
                }
              });
            }
            
            // Create new activity for this transportation using the utility function
            const newActivity = createNewActivity({
              type: 'transportation',
              name: transportation.name,
              description: transportation.description || '',
              fromLocation: transportation.fromLocation || { address: '', latitude: '', longitude: '' },
              toLocation: transportation.toLocation || { address: '', latitude: '', longitude: '' },
              startDateTime: transportation.startDate,
              endDateTime: transportation.endDate,
              estimatedDuration: transportation.estimatedDuration || '',
              links: links,
              defaultCurrency: parsedData?.currency || DEFAULT_CURRENCY
            });
            
            // Add to the matching day
            updatedData.days[matchingDayIndex].activities.push(newActivity);
            needsUpdate = true;
          }
        }
      });
      
      // Save changes if needed
      if (needsUpdate) {
        await saveChanges(updatedData);
      }
    } catch (error) {
      cerror("Error processing transportations:", error);
    } finally {      
    }
  };
 
  // Function to open modal for adding a new activity with specific type
  const handleAddActivityWithType = (dayId, activityType) => {
    setSelectedActivity(null);
    setSelectedDayId(dayId);
    setIsNewActivity(true);
    setCurrentActivityType(activityType);
    setShowModal(true);
  };
  
  // Function to open modal for editing an activity
  const handleEditActivity = (activity, dayId) => {
    setSelectedActivity(activity);
    setSelectedDayId(dayId);
    setIsNewActivity(false);
    setCurrentActivityType(activity.type);
    setShowModal(true);
  };
  
  // Function to open delete confirmation for an activity
  const handleConfirmDelete = (activity, dayId) => {
    setActivityToDelete(activity);
    setDayIdOfActivityToDelete(dayId);
    setShowDeleteModal(true);
  };
  
  // Function to actually delete an activity
  const handleDeleteActivity = async () => {
    if (!activityToDelete || !dayIdOfActivityToDelete) return;
    
    try {
      // Find the day and remove the activity
      const updatedData = {
        ...parsedData,
        days: parsedData.days.map(day => {
          if (day.id === dayIdOfActivityToDelete) {
            return {
              ...day,
              activities: day.activities.filter(a => a.id !== activityToDelete.id)
            };
          }
          return day;
        })
      };
      
      // Resort activities to ensure consistent ordering 
      updatedData.days.forEach(day => {
        if (day.id === dayIdOfActivityToDelete) {
          day.activities = sortActivitiesByTime(day.activities);
        }
      });
      
      // Save changes to the backend
      const saved = await saveChanges(updatedData);
      
      if (saved) {
        // Close modal only on successful save
        setShowDeleteModal(false);
        setActivityToDelete(null);
        setDayIdOfActivityToDelete(null);
      }
      // Error is already displayed by saveChanges via setError
    } catch (error) {
      cerror('Error deleting activity:', error);
      // This should not happen now since saveChanges doesn't throw, but keep as safety net
      if (!error.message || !error.message.includes('VERSION_MISMATCH')) {
        setError('Failed to delete activity. Please try again.');
      }
    }
  };
  
  // Function to handle sharing a plan
  const handleSharePlan = async () => {
    // Check permission first
    if (!canShare) {
      setUpgradeMessage(t('planDetail.upgrade.sharingTrips'));
      setShowUpgradeModal(true);
      return;
    }
    
    if (!parsedData) return;
    
    // Show share options modal (it will handle loading saved data or defaults via useEffect)
    setShowShareOptionsModal(true);
  };

  // Function to handle PDF download
  const handleDownloadPDF = async () => {
    if (!canExportPDF) {
      setUpgradeMessage(t('planDetail.upgrade.pdfExport'));
      setShowUpgradeModal(true);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication required. Please log in again.');
        return;
      }

      // Show loading state for PDF generation (not saving!)
      setIsGeneratingPDF(true);
      
      const response = await fetch(`/api/get?action=${ApiActions.EXPORT_PLAN_PDF}&planId=${planId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      // Check content type to determine if it's an error (JSON) or PDF
      const contentType = response.headers.get('content-type');
      
      if (!response.ok || (contentType && contentType.includes('application/json'))) {
        // It's an error response (JSON)
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to generate PDF');
      }

      // Get PDF blob
      const blob = await response.blob();
      
      // Verify it's actually a PDF (check blob type or size)
      if (blob.size === 0) {
        throw new Error('Received empty PDF file');
      }
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${parsedData?.name || 'trip'}_itinerary.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      cerror('Error downloading PDF:', error);
      setError(error.message || 'Failed to download PDF. Please try again.');
      setShowErrorModal(true);
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  // Callback to update plan state from ShareOptionsModal
  const handlePlanUpdate = (updatedPlan) => {
    setPlan(updatedPlan);
  };
  
  
  // Function to handle sharing to groups
  const handleShareToGroups = async () => {
    if (!canManageGroups) {
      setUpgradeMessage(t('planDetail.upgrade.sharingToGroups'));
      setShowUpgradeModal(true);
      return;
    }
    
    if (!parsedData) return;
    
    // Clear any previous messages
    setShareToGroupMessage({ type: null, text: null });
    setShareToGroupExpandedSections({ transportation: false, accommodation: false });
    
    // Set defaults first
    setShareToGroupExclusions({ excludedActivityIds: [] });
    setShareToGroupOptions({
      includeTodos: true,
      includeCosts: true,
      includeNotes: true,
      includePackingList: true,
      includeInfo: true
    });
    setShareToGroupEmailNotification('new'); // Default: notify new emails only
    setSelectedGroupIds([]);
    
    // Override with saved values if plan was already shared
    if (plan?.sharingData?.groupShare) {
      const groupShare = plan.sharingData.groupShare;
      const excludeIds = (groupShare.excludeIds || []).map(id => String(id));
      setShareToGroupExclusions({ excludedActivityIds: excludeIds });
      setShareToGroupOptions({
        includeTodos: !groupShare.cleanSectionsData?.todo,
        includeCosts: !groupShare.cleanSectionsData?.cost,
        includeNotes: !groupShare.cleanSectionsData?.notes,
        includePackingList: !groupShare.cleanSectionsData?.packing,
        includeInfo: !groupShare.cleanSectionsData?.info
      });
    }
    
    // Auto-select groups if plan is already shared
    if (plan?.sharedWithGroups && plan.sharedWithGroups.length > 0) {
      const groupIds = plan.sharedWithGroups.map(id => id.toString ? id.toString() : String(id));
      setSelectedGroupIds(groupIds);
    }
    
    // Always show share to group modal (it will handle groups loading and display message if needed)
    setShowShareToGroupModal(true);
  };
  
  // Function to proceed with sharing to groups (extraOptions may contain { banner } from ShareToGroupModal)
  const handleProceedWithShareToGroups = async (extraOptions = {}) => {
    if (!parsedData) return;
    
    if (selectedGroupIds.length === 0) {
      setShareToGroupMessage({ type: 'error', text: 'Please select at least one group to share with.' });
      return;
    }
    
    // Clear any previous messages
    setShareToGroupMessage({ type: null, text: null });
    setIsSharingToGroups(true);
    
    try {
      // Clean the plan data for group sharing with exclusions and options (using COPY)
      const cleanedPlanData = cleanPlanForGroupSharing(parsedData, shareToGroupExclusions, shareToGroupOptions);
      
      const banner = extraOptions.banner && extraOptions.banner.url ? extraOptions.banner : undefined;
      
      // Prepare sharingData for group share
      // Convert all activity IDs to strings for consistent storage
      const excludeIds = (shareToGroupExclusions.excludedActivityIds || []).map(id => String(id));
      const sharingData = {
        groupShare: {
          excludeIds: excludeIds,
          banner,
          cleanSectionsData: {
            cost: !shareToGroupOptions.includeCosts,
            todo: !shareToGroupOptions.includeTodos,
            notes: !shareToGroupOptions.includeNotes,
            packing: !shareToGroupOptions.includePackingList,
            info: !shareToGroupOptions.includeInfo
          }
        }
      };
      
      clog('[DEBUG] Saving sharingData:', {
        excludeIds: excludeIds,
        excludeIdsLength: excludeIds.length,
        shareToGroupExclusions: shareToGroupExclusions,
        cleanSectionsData: sharingData.groupShare.cleanSectionsData,
        shareToGroupOptions: shareToGroupOptions
      });
      
      // Call the shared API to share to groups
      const response = await apiCall('/api/shared', {
        action: 'SHARE_TO_GROUPS',
        originalPlanId: planId,
        cleanedData: cleanedPlanData,
        planName: parsedData.name || 'Shared Trip Plan',
        groupIds: selectedGroupIds,
        sharingData: sharingData,
        emailNotification: shareToGroupEmailNotification // 'new', 'all', or 'none'
      }, 'POST');
      
      if (response.success) {
        // Update plan state with new sharingData
        setPlan(prevPlan => ({
          ...prevPlan,
          sharingData: {
            ...prevPlan.sharingData,
            groupShare: sharingData.groupShare
          },
          sharedWithGroups: response.sharedWithGroups || prevPlan.sharedWithGroups
        }));
        
        // Show success message in modal (don't close it)
        setShareToGroupMessage({ type: 'success', text: t('planDetail.shareToGroups.planSharedSuccessfully') });
      } else {
        setShareToGroupMessage({ type: 'error', text: response.message || t('planDetail.shareToGroups.failedToShare') });
      }
    } catch (error) {
      cerror('Error sharing plan to groups:', error);
      setShareToGroupMessage({ type: 'error', text: t('planDetail.shareToGroups.failedToShare') });
    } finally {
      setIsSharingToGroups(false);
    }
  };
  
  // Function to save or update an activity
  const handleSaveActivity = async (formData, dayId) => {
    try {
      // Create a copy of the parsed data
      const updatedData = { ...parsedData };
      
      // Find the day to update
      const dayIndex = updatedData.days.findIndex(day => day.id === dayId);
      if (dayIndex === -1) throw new Error('Day not found');
      
      // Process activity data with our utility function to ensure consistency
      const processedActivity = createNewActivity({
        id: formData.id,
        type: formData.type,
        name: formData.name,
        nameInEnglish: formData.nameInEnglish,
        description: formData.description,
        notes: formData.notes,
        startDateTime: formData.startDateTime,
        endDateTime: formData.endDateTime,
        checkInTime: formData.checkInTime,
        checkOutTime: formData.checkOutTime,
        cost: formData.cost,
        location: formData.location,
        fromLocation: formData.fromLocation,
        toLocation: formData.toLocation,
        estimatedDuration: formData.estimatedDuration,
        links: formData.links,
        images: formData.images,
        option: formData.option !== undefined ? formData.option : [], // Preserve option field (empty array = option 1)
        descriptionAlign: formData.descriptionAlign,
        defaultCurrency: parsedData?.currency || DEFAULT_CURRENCY
      });
      
      // Debug: Log option field to verify it's being saved
      if (processedActivity.option !== undefined) {
        clog('Saving activity with option:', processedActivity.option, 'for activity:', processedActivity.name);
      }
      
      // For accommodations from AI JSON, ensure we have all data properly set
      if (processedActivity.type === 'accommodation') {
        // Make sure end date is set for accommodations
        if (!processedActivity.endDateTime && parsedData.accommodations) {
          // Try to find this accommodation in the original JSON data
          const matchingAccom = parsedData.accommodations.find(accom => 
            accom.name === processedActivity.name || processedActivity.name.includes(accom.name)
          );
          
          if (matchingAccom) {
            clog("Found matching accommodation in JSON:", matchingAccom.name);
            clog("Check-in time:", matchingAccom.checkInTime);
            clog("Check-out time:", matchingAccom.checkOutTime);
            // Use the endDate from the AI JSON
            processedActivity.endDateTime = matchingAccom.endDate;
            
            // Set check-in/check-out times from the matching accommodation
            if (matchingAccom.checkInTime) {
              processedActivity.checkInTime = matchingAccom.checkInTime;
            }
            
            if (matchingAccom.checkOutTime) {
              processedActivity.checkOutTime = matchingAccom.checkOutTime;
            }
            
            // Add location if not present
            if (!processedActivity.location && matchingAccom.location) {
              processedActivity.location = matchingAccom.location;
            }
            
            // Ensure name is kept as is from the AI JSON (without "Check-in:" prefix)
            if (processedActivity.name.startsWith("Check-in: ")) {
              processedActivity.name = processedActivity.name.replace("Check-in: ", "");
            }
            
            // Add website as link if available
            if (matchingAccom.website && (!processedActivity.links || processedActivity.links.length === 0)) {
              processedActivity.links = [{
                name: "Website",
                url: matchingAccom.website
              }];
            }
          }
        }
      }
      
      // If editing existing activity
      if (!isNewActivity) {
        // Find the original activity
        const originalActivity = updatedData.days[dayIndex].activities.find(
          activity => activity.id === processedActivity.id
        );
        
        if (originalActivity) {
          // Get date parts to check if the date has changed
          const originalDateStr = originalActivity.startDateTime ? 
            originalActivity.startDateTime.split('T')[0] : null;
          const newDateStr = processedActivity.startDateTime ? 
            processedActivity.startDateTime.split('T')[0] : null;
          
          // Check if start date has been changed to a different day
          if (originalDateStr && newDateStr && originalDateStr !== newDateStr) {
            clog(`Date changed from ${originalDateStr} to ${newDateStr}`);
            
            // Find the correct day for the new date
            const targetDay = updatedData.days.find(day => {
              const dayDate = day.date ? day.date.split('T')[0] : null;
              return dayDate === newDateStr;
            });
            
            if (targetDay && targetDay.id !== dayId) {
              clog(`Moving activity to day ${targetDay.id}`);
              
              // Use the shared function to move the activity
              const moved = await moveActivityBetweenDays(
                processedActivity, 
                dayId, 
                targetDay.id, 
                newDateStr
              );
              
              if (moved) {
                // Close the modal
                setShowModal(false);
                setSelectedActivity(null);
                setSelectedDayId(null);
                return;
              }
            }
          }

          if (originalActivity.descriptionAlign) {
            processedActivity.descriptionAlign = originalActivity.descriptionAlign;
          }
        }
        
        // If not moving to a different day, update in place
        updatedData.days[dayIndex].activities = updatedData.days[dayIndex].activities.map(activity => 
          activity.id === processedActivity.id ? processedActivity : activity
        );
      } 
      // If adding new, push to activities array
      else {
        updatedData.days[dayIndex].activities.push(processedActivity);
      }
      
      // Sort activities by time to ensure proper order
      updatedData.days[dayIndex].activities = sortActivitiesByTime(updatedData.days[dayIndex].activities);
      
      // Save changes to the backend
      const saved = await saveChanges(updatedData);
      
      if (saved) {
        // Close modal and reset state only on successful save
        setShowModal(false);
        setSelectedActivity(null);
        setSelectedDayId(null);
      }
      // Error is already displayed by saveChanges via setError
    } catch (error) {
      cerror('Error saving activity:', error);
      // This should not happen now since saveChanges doesn't throw, but keep as safety net
      if (!error.message || !error.message.includes('VERSION_MISMATCH')) {
        setError('Failed to save activity. Please try again.');
      }
    }
  };
  
  // Function to save plan changes to the backend
  const saveChanges = async (updatedData, params = {}) => {
    setIsSaving(true);
    try {
      const requestData = {
        planId: planId,
        data: JSON.stringify(updatedData),
        version: planVersion // Send current version
      };
      
      // If we're updating the plan name in the database collection,
      // include it as a separate parameter
      if (params.updatePlanName && params.newName) {
        requestData.name = params.newName;
      }
      
      // If we're updating the cover image, include it as a separate parameter
      if (params.coverImage !== undefined) {
        requestData.coverImage = params.coverImage || '';
      }
      
      // Extract and send startDate and endDate to update the plan document
      // These are used for sorting plans on the trips/plans page
      if (updatedData.startDate) {
        // Extract just the date part (YYYY-MM-DD) and convert to Date
        const startDateStr = updatedData.startDate.split('T')[0];
        requestData.startDate = new Date(startDateStr + 'T00:00:00.000Z');
      }
      if (updatedData.endDate) {
        // Extract just the date part (YYYY-MM-DD) and convert to Date
        const endDateStr = updatedData.endDate.split('T')[0];
        requestData.endDate = new Date(endDateStr + 'T23:59:59.999Z');
      }
      
      // Use POST method for updates
      const response = await apiCall("/api/model?action=" + ApiActions.UPDATE_PLAN, requestData, 'POST');
      
      if (response.success) {
        // Update local state
        setParsedData(updatedData);
        
        // Update version from server response (only on successful save)
        if (response.plan && response.plan.version) {
          setPlanVersion(response.plan.version);
        }
        
        // If plan name was updated, update the plan object too
        if (params.updatePlanName && params.newName) {
          setPlan(prevPlan => ({
            ...prevPlan,
            name: params.newName
          }));
        }
        
        // If cover image was updated, update the plan object too
        if (params.coverImage !== undefined) {
          setPlan(prevPlan => ({
            ...prevPlan,
            coverImage: params.coverImage || ''
          }));
        }
        
        return true; // Success
      } else {
        // Check if it's a version mismatch error
        if (response.message && response.message.includes('Version mismatch')) {
          throw new Error('VERSION_MISMATCH: ' + response.message);
        }
        throw new Error(response.message || 'Failed to update plan');
      }
    } catch (error) {
      cerror('Error saving plan:', error);
      
      // Handle version mismatch error with user-friendly message
      // Check both for the VERSION_MISMATCH prefix and the actual error message from API
      if (error.message && (error.message.includes('VERSION_MISMATCH') || error.message.includes('Version mismatch'))) {
        const errorMsg = error.message.replace('VERSION_MISMATCH: ', '');
        setError(errorMsg);
      } else {
        setError('Failed to save changes. Please try again.');
      }
      // Don't throw - error is already displayed to user via setError
      // Return false to indicate failure
      return false;
    } finally {
      setIsSaving(false);
    }
  };


  const getDatePart = (dateTime) => {
    if (!dateTime || typeof dateTime !== 'string') return '';
    return dateTime.split('T')[0] || '';
  };

  // Render a simplified version of accommodation status cards at the top of each day
  const renderAccommodationStatusCard = (activity, label) => {
    return (
      <Card className="mb-2 shadow-sm border-success">
        <Card.Body className="py-2 px-2 px-md-3">
          <div className="d-flex align-items-center">
            <i className="bi bi-house-door me-2 text-success"></i>
            <span><strong>{label}:</strong> {activity.name}</span>
          </div>
        </Card.Body>
      </Card>
    );
  };

  // Build transportation segments once (using same logic as transportation tab)
  // Returns segments and lookup maps for activity route links
  const transportationSegments = useMemo(() => {
    if (!parsedData) return { segments: [], nextActivityMap: new Map(), previousActivityMap: new Map() };
    return buildTransportationSegments(parsedData);
  }, [parsedData]);
  
  const nextActivityMap = transportationSegments.nextActivityMap;
  const previousActivityMap = transportationSegments.previousActivityMap;

  const isDayCollapsedByDefault = (dayId, dayPlanReady = false) => {
    if (collapsedDays[dayId] !== undefined) {
      return collapsedDays[dayId] === true;
    }
    return dayPlanReady === true;
  };

  // Replace the existing renderActivityCard function with the new component approach
  const renderActivityCard = (activity, dayId, dayPlanReady = false) => {
    const isDayCollapsed = isDayCollapsedByDefault(dayId, dayPlanReady);
    const nextActivityInfo = nextActivityMap.get(activity.id) || null;
    const previousActivityInfo = previousActivityMap.get(activity.id) || null;
    return (
      <div className="activity-card">
        {renderActivity(
          activity, 
          dayId, 
          handleEditActivity, 
          handleConfirmDelete,
          updateActivityCompletionStatus,
          updateActivityDescriptionAlign,
          parsedData?.settings?.collapseCompletedActivities,
          isDayCollapsed,
          nextActivityInfo,
          previousActivityInfo
        )}
      </div>
    );
  };
  
  // Function to toggle collapse/expand all activities for a day
  const toggleDayActivities = (dayId, dayPlanReady = false) => {
    const currentState = isDayCollapsedByDefault(dayId, dayPlanReady);
    setCollapsedDays(prev => ({
      ...prev,
      [dayId]: !currentState
    }));
  };

  const toggleDayPlanReady = async (dayId) => {
    if (!parsedData?.days) return;

    const targetDay = parsedData.days.find(day => day.id === dayId);
    if (!targetDay) return;

    const newPlanReadyState = targetDay.planReady !== true;

    const updatedData = {
      ...parsedData,
      days: parsedData.days.map(day => (
        day.id === dayId ? { ...day, planReady: newPlanReadyState } : day
      ))
    };

    const saved = await saveChanges(updatedData);
    if (!saved) return;

    // Keep ready days collapsed immediately after marking them ready.
    if (newPlanReadyState) {
      setCollapsedDays(prev => ({
        ...prev,
        [dayId]: true
      }));
    }
  };

  // Function to open suggested attractions modal
  const handleOpenSuggestions = (dayId) => {
    setSelectedDayId(dayId);
    
    // Use the suggestedAttractions directly from the plan data
    if (parsedData?.suggestedAttractions && Array.isArray(parsedData.suggestedAttractions)) {
      setSuggestedAttractions(parsedData.suggestedAttractions);
    }
    
    setShowSuggestionsModal(true);
  };
  
  // Function to regenerate suggested attractions using AI
  const handleRegenerateSuggestions = async () => {
    if (!parsedData) return;
    
    setIsLoadingSuggestions(true);
    setError('');
    
    try {
      // Build context for AI from plan data
      // Extract destination from plan name or description
      const planName = parsedData.name || '';
      const planDescription = parsedData.description || '';
      const startDate = parsedData.startDate ? safeFormatDate(parsedData.startDate) : '';
      const endDate = parsedData.endDate ? safeFormatDate(parsedData.endDate) : '';
      const tripDuration = parsedData.days?.length || 0;
      const groupInfo = parsedData.groupInfo || '';
      
      // Build comprehensive context including all available plan information
      let prompt = `Plan: ${planName}.`;
      if (planDescription) {
        prompt += ` ${planDescription}`;
      }
      if (groupInfo) {
        prompt += ` Travelers: ${groupInfo}.`;
      }
      if (startDate && endDate) {
        prompt += ` Trip dates: ${startDate} to ${endDate}.`;
      }
      if (tripDuration > 0) {
        prompt += ` Duration: ${tripDuration} days.`;
      }
      prompt += ' Please suggest attractions and points of interest for this trip.';
      
      // Call AI API with attractions template
      const response = await apiCall("/api/rawAi", { 
        prompt: prompt,
        context: templates.getAttractionsTemplate(i18n.language, 20),
        systemContent: templates.systemContext.getTripPlannerAssistant(i18n.language),
        feature: 'regenerate_suggested_attractions',
        language: i18n.language
      }, 'POST');
      
      if (response.success && response.rawContent) {
        try {
          // Parse the AI response (tryParseJson handles markdown cleaning internally)
          const { jsonData, result } = tryParseJson(response.rawContent);
          if (!result || !jsonData) {
            throw new Error('Failed to parse AI response');
          }
          const parsedJson = jsonData;
          
          // Extract attractions
          let attractions = parsedJson.suggestedAttractions || parsedJson.SuggestedAttractions || [];
          
          // Normalize locations
          attractions = attractions.map(attraction => ({
            ...attraction,
            location: typeof attraction.location === 'string'
              ? { address: attraction.location, latitude: '', longitude: '' }
              : (attraction.location || { address: '', latitude: '', longitude: '' })
          }));
          
          // Fetch images for attractions
          if (attractions.length > 0) {
            const enhancedAttractions = await fetchAttractionImages(attractions);
            setSuggestedAttractions(enhancedAttractions);
            
            // Update plan data with new suggestions
            const updatedData = {
              ...parsedData,
              suggestedAttractions: enhancedAttractions
            };
            await saveChanges(updatedData);
          } else {
            setError('No attractions were generated. Please try again.');
          }
        } catch (parseError) {
          cerror('Error parsing AI response:', parseError);
          setError('Failed to process AI response. Please try again.');
        }
      } else {
        setError(response.message || 'Failed to generate attractions. Please try again.');
      }
    } catch (err) {
      cerror('Error regenerating suggestions:', err);
      setError('An error occurred while generating suggestions. Please try again.');
    } finally {
      setIsLoadingSuggestions(false);
    }
  };
  
  // Function to open day map modal
  const handleOpenDayMap = (day, dayIndex) => {
    setSelectedDayForMap(day);
    setSelectedDayIndexForMap(dayIndex);

    // Prepare continuing accommodations for this day (same logic as day view)
    const multiDayAccoms = findMultiDayAccommodations(parsedData);
    const continueAccoms = multiDayAccoms.filter(accom =>
      isDayWithinAccommodationRange(day.date, accom.startDateTime, accom.endDateTime) &&
      accom.originalDay !== day.date
    );
    setContinuingAccommodationsForMap(continueAccoms);

    setShowDayMapModal(true);
  };

  const handleOpenDayShortsMap = (day, dayIndex) => {
    setSelectedDayForShortsMap(day);
    setSelectedDayIndexForShortsMap(dayIndex);
    setShowDayShortsMapModal(true);
  };

  const handleOpenDayShortsStats = (day, dayIndex) => {
    setSelectedDayForShortsStats(day);
    setSelectedDayIndexForShortsStats(dayIndex);
    setShowDayShortsStatsModal(true);
  };

  const handleSaveDayShortsStats = async (dayId, stats) => {
    if (!parsedData?.days || !dayId) return;
    const dayIndex = parsedData.days.findIndex((d) => d.id === dayId);
    if (dayIndex === -1) return;
    const updatedData = {
      ...parsedData,
      days: parsedData.days.map((d, i) =>
        i === dayIndex ? { ...d, shortsStats: stats } : d
      ),
    };
    await saveChanges(updatedData);
  };

  const getActivityTypeLabel = (type) => {
    switch (type) {
      case 'accommodation':
        return t('planDetail.days.accommodation');
      case 'transportation':
        return t('planDetail.days.transportation');
      case 'attraction':
      default:
        return t('planDetail.days.attraction');
    }
  };

  const getActivityDisplayName = (activity) => {
    const name = activity.name?.trim() || activity.nameInEnglish?.trim();
    if (!name) return t('planDetail.days.notSpecified');
    if (activity.nameInEnglish?.trim() && activity.name?.trim() && activity.name !== activity.nameInEnglish) {
      return `${activity.name} (${activity.nameInEnglish})`;
    }
    return name;
  };

  const handleCopyActivitiesToChat = async (day, dayIndex) => {
    const selectedOption = dayOptionFilters[day.id] !== undefined ? dayOptionFilters[day.id] : 1;
    const filteredActivities = day.activities
      ? day.activities.filter((activity) => activityMatchesOption(activity, selectedOption))
      : [];

    if (filteredActivities.length === 0) {
      setError(t('planDetail.days.copyActivitiesToChat.noActivities'));
      setAlertVariant('warning');
      setShowErrorModal(true);
      return;
    }

    const dayNumber = dayIndex + 1;
    const dateLabel = day.date ? formatDateWithLocale(day.date, 'EEEE, PPP') : '';
    const intro = t('planDetail.days.copyActivitiesToChat.promptIntro', { dayNumber, date: dateLabel });
    const items = filteredActivities
      .map((activity, idx) =>
        t('planDetail.days.copyActivitiesToChat.promptItem', {
          index: idx + 1,
          name: getActivityDisplayName(activity),
          type: getActivityTypeLabel(activity.type),
        })
      )
      .join('\n');
    const outro = parsedData.settings?.activitiesChatPromptContext?.trim()
      || t('planDetail.days.copyActivitiesToChat.promptOutro');
    const promptText = `${intro}\n\n${items}\n\n${outro}`;

    try {
      await navigator.clipboard.writeText(promptText);
      setShowGeminiNavigateModal(true);
    } catch (err) {
      cerror('Failed to copy activities prompt to clipboard:', err);
      setError(t('planDetail.days.copyActivitiesToChat.copyFailed'));
      setAlertVariant('error');
      setShowErrorModal(true);
    }
  };

  const handleSwapDays = async (sourceDayId, targetDayId) => {
    if (!parsedData?.days) return;
    const dayA = parsedData.days.find((d) => d.id === sourceDayId);
    const dayB = parsedData.days.find((d) => d.id === targetDayId);
    if (!dayA || !dayB) return;
    const dateA = (dayA.date || '').split('T')[0];
    const dateB = (dayB.date || '').split('T')[0];
    if (!dateA || !dateB) return;
    const activitiesForA = (dayB.activities || []).map((activity) => updateActivityDateTime(activity, dateA));
    const activitiesForB = (dayA.activities || []).map((activity) => updateActivityDateTime(activity, dateB));
    const swappedDays = parsedData.days.map((d) => {
      if (d.id === sourceDayId) {
        return {
          ...d,
          activities: sortActivitiesByTime(activitiesForA),
          todos: dayB.todos || [],
          summaryTitle: dayB.summaryTitle || '',
          summary: dayB.summary || '',
          summaryCollapsed: dayB.summaryCollapsed === true
        };
      }
      if (d.id === targetDayId) {
        return {
          ...d,
          activities: sortActivitiesByTime(activitiesForB),
          todos: dayA.todos || [],
          summaryTitle: dayA.summaryTitle || '',
          summary: dayA.summary || '',
          summaryCollapsed: dayA.summaryCollapsed === true
        };
      }
      return d;
    });

    const updatedData = {
      ...parsedData,
      days: swappedDays
    };
    const saved = await saveChanges(updatedData);
    if (saved) setSuccessMessage(t('planDetail.days.swapDays.success', { defaultValue: 'Days swapped successfully.' }));
  };

  const handleSaveDaySummary = async (dayId, { summaryTitle = '', summary = '' } = {}) => {
    if (!parsedData?.days || !dayId) return;
    const dayIndex = parsedData.days.findIndex((d) => d.id === dayId);
    if (dayIndex === -1) return;
    const updatedDay = {
      ...parsedData.days[dayIndex],
      summaryTitle: summaryTitle || '',
      summary: summary || ''
    };
    if (!summaryTitle.trim() && !summary.trim()) {
      delete updatedDay.summaryCollapsed;
    }
    const updatedData = {
      ...parsedData,
      days: parsedData.days.map((d, i) => (i === dayIndex ? updatedDay : d))
    };
    await saveChanges(updatedData);
  };

  // Function to check if an attraction has already been added to the plan
  const isAttractionAlreadyAdded = (attraction) => {
    if (!parsedData || !parsedData.days) return false;
    
    // Get attraction ID and normalize name
    const attractionId = attraction.id;
    const attractionName = attraction.name?.toLowerCase().trim();
    
    if (!attractionName) return false;
    
    // Check all days and activities
    for (const day of parsedData.days) {
      if (!day.activities) continue;
      
      // Look for a matching attraction by ID or name
      const found = day.activities.some(activity => {
        // Skip non-attraction activities
        if (activity.type !== 'attraction') return false;
        
        // First, try exact ID match if both have IDs
        if (attractionId && activity.id && activity.id === attractionId) {
          return true;
        }
        
        // Then try name match
        const activityName = activity.name?.toLowerCase().trim();
        if (!activityName) return false;
        
        // Exact name match
        if (activityName === attractionName) {
          return true;
        }
        
        // For partial matches, be more strict to avoid false positives
        // Only match if one is a significant substring of the other (>75% of characters)
        if (activityName.includes(attractionName) || attractionName.includes(activityName)) {
          const shorterName = activityName.length < attractionName.length ? activityName : attractionName;
          const longerName = activityName.length >= attractionName.length ? activityName : attractionName;
          
          // If the shorter name is very short, require exact match
          if (shorterName.length < 5) return false;
          
          // Calculate similarity percentage - require at least 75% match
          const similarity = shorterName.length / longerName.length;
          return similarity > 0.75;
        }
        
        return false;
      });
      
      if (found) return true;
    }
    
    return false;
  };

  // Function to add a suggested attraction to the selected day
  const handleAddSuggestedAttraction = async (attraction) => {
    try {
      const updatedData = { ...parsedData };
      
      // Find the day to update
      const dayIndex = updatedData.days.findIndex(day => day.id === selectedDayId);
      if (dayIndex === -1) throw new Error('Day not found');
      
      // Get the day's date
      const dayDate = updatedData.days[dayIndex].date;
      if (!dayDate) throw new Error('Day has no date');
      
      // Parse duration if available (convert to minutes for consistency)
      let durationMinutes = 120; // Default to 2 hours
      if (attraction.estimatedDuration) {
        if (attraction.estimatedDuration.includes('hour')) {
          const hoursMatch = attraction.estimatedDuration.match(/(\d+(\.\d+)?)-?(\d+(\.\d+)?)?/);
          if (hoursMatch) {
            // If range like "2-3 hours", take the average
            if (hoursMatch[3]) {
              durationMinutes = Math.round(((parseFloat(hoursMatch[1]) + parseFloat(hoursMatch[3])) / 2) * 60);
            } else {
              durationMinutes = Math.round(parseFloat(hoursMatch[1]) * 60);
            }
          }
        } else if (attraction.estimatedDuration.includes('minute')) {
          const minutesMatch = attraction.estimatedDuration.match(/(\d+)/);
          if (minutesMatch) {
            durationMinutes = parseInt(minutesMatch[1]);
          }
        }
      }
      
      // Get existing activities for this day (including accommodations)
      const existingActivities = updatedData.days[dayIndex].activities;
      
      // Calculate start and end datetime using shared utility (string-based, no Date objects)
      const { startDateTime, endDateTime } = calculateActivityDateTime(dayDate, existingActivities, durationMinutes);
      
      // Create the new activity using unified function (automatically adds Google Maps links, excludes estimatedDuration)
      const newActivity = createNormalizedActivity({
        type: 'attraction',
        id: attraction.id || generateUUID(),
        name: attraction.name,
        nameInEnglish: attraction.nameInEnglish || null,
        description: attraction.description || '',
        notes: attraction.notes || '',
        startDateTime,
        endDateTime,
        location: attraction.location || { address: '', latitude: '', longitude: '' },
        website: attraction.website,
        links: attraction.links || [],
        // Preserve the image data from the suggested attraction
        images: attraction.images || []
      }, {
        addGoogleMapsLinks: true,
        includeEstimatedDuration: false
      });
      
      // Add to the day's activities
      updatedData.days[dayIndex].activities.push(newActivity);
      
      // Sort activities by time
      updatedData.days[dayIndex].activities = sortActivitiesByTime(updatedData.days[dayIndex].activities);
      
      // Save changes
      const saved = await saveChanges(updatedData);
      
      if (saved) {
        // Close modal only on successful save
        setShowSuggestionsModal(false);
      }
      // Error is already displayed by saveChanges via setError
    } catch (error) {
      cerror('Error adding suggested attraction:', error);
      // This should not happen now since saveChanges doesn't throw, but keep as safety net
      if (!error.message || !error.message.includes('VERSION_MISMATCH')) {
        setError('Failed to add attraction. Please try again.');
      }
    }
  };

  // Function to open AI Activity modal (used both for direct open and switching from manual modal)
  const handleOpenAiModal = (dayId, activityType = 'attraction', closeManualModal = false) => {
    if (closeManualModal) {
      setShowModal(false);
    }
    setSelectedDayId(dayId);
    setAiPrompt('');
    setGeneratedActivity(null);
    setGeneratedActivities([]);
    setSelectedActivityIndex(0);
    setAiError('');
    setIsGeneratingActivity(false);
    setAiActivityType(activityType);
    setUseTripContext(true); // Reset to default
    setShowAiModal(true);
  };
  
  // Function to generate activity using AI
  const handleGenerateActivity = async () => {
    if (!aiPrompt.trim()) {
      setAiError(t('planDetail.aiActivity.descriptionRequired'));
      return;
    }
    
    setIsGeneratingActivity(true);
    setAiError('');
    setGeneratedActivities([]);
    setSelectedActivityIndex(0);
    
    try {      
      
      // Prepare user input with relevant context
      let promptTemplate;
      
      // Determine which template to use based on selected activity type
      switch (aiActivityType) {
        case 'accommodation':
          promptTemplate = templates.getAccommodations(i18n.language, 3); // Request 3 accommodation options
          break;
        case 'transportation':
          promptTemplate = templates.getTransportations(i18n.language, 3); // Request 3 transportation options
          break;
        case 'attraction':
          promptTemplate = templates.getAttractionsTemplate(i18n.language, 3); // Request 3 attraction options
          break;
        default:
          promptTemplate = templates.getAttractionsTemplate(i18n.language, 3);
      }
      
      // Build user input with optional trip context
      let userInput = aiPrompt;
      if (useTripContext && parsedData?.name) {
        userInput = `${aiPrompt}\n\nTrip context: ${parsedData.name}`;
      }
      
      // Call the AI API
      const response = await apiCall("/api/rawAi", { 
        prompt: userInput,
        context: promptTemplate,
        systemContent: templates.systemContext.getTripPlannerAssistant(i18n.language),
        feature: 'activity_generation',
        language: i18n.language
      }, 'POST');
      
      if (response.success && response.rawContent) {
        try {
          // Parse the JSON using tryParseJson (same approach as CreateTripV2)
          const { jsonData, result } = tryParseJson(response.rawContent);
          
          if (!result || !jsonData) {
            // This error will be caught and replaced with user-friendly message below
            throw new Error('Invalid response from AI');
          }
          
          const parsedJson = jsonData;
          
          // Extract the activities array with the right key based on activity type
          let activities;
          if (aiActivityType === 'accommodation') {
            activities = parsedJson.Accommodations || parsedJson.accommodations || [];
            
            // Ensure each accommodation has the required properties
            activities = activities.map(accommodation => ({
              ...accommodation,
              location: normalizeLocation(accommodation.location),
              startDate: accommodation.startDate || '',
              endDate: accommodation.endDate || ''
            }));
          } else if (aiActivityType === 'transportation') {
            activities = parsedJson.Transportations || parsedJson.transportations || [];
            
            if (activities.length === 0) {
              throw new Error("We couldn't generate the transportation. Try to be more specific (like 'flight from Paris to London', 'Train from...').");
            }
            
            // Ensure each transportation has the required properties
            activities = activities.map(transportation => ({
              ...transportation,
              fromLocation: normalizeLocation(transportation.fromLocation),
              toLocation: normalizeLocation(transportation.toLocation),
              startDate: transportation.startDate || '',
              endDate: transportation.endDate || ''
            }));
          } else {
            activities = parsedJson.suggestedAttractions || [];
          }
          
          if (activities.length > 0) {
            // Fetch images for the suggested attractions
            const enhancedActivities = await fetchAttractionImages(activities);
            // Set the generated activities with images
            const limitedActivities = enhancedActivities.slice(0, 3);
            setGeneratedActivities(limitedActivities);
            setGeneratedActivity(limitedActivities[0]);
          } else {
            // User-friendly error when no activities found
            if (aiActivityType === 'transportation') {
              throw new Error("We couldn't generate the transportation. Try to be more specific (like 'flight from Paris to London', 'Train from...').");
            } else if (aiActivityType === 'accommodation') {
              throw new Error("We couldn't generate the accommodation. Try to be more specific (like 'hotel in Paris', 'Airbnb in London').");
            } else {
              throw new Error(`No valid ${aiActivityType} options found. Please try again with a more specific description.`);
            }
          }
          
        } catch (err) {
          // User-friendly error messages based on activity type
          if (aiActivityType === 'transportation') {
            setAiError("We couldn't generate the transportation. Try to be more specific (like 'flight from Paris to London', 'Train from...').");
          } else if (aiActivityType === 'accommodation') {
            setAiError("We couldn't generate the accommodation. Try to be more specific (like 'hotel in Paris', 'Airbnb in London').");
          } else {
            setAiError("We couldn't generate the activity. Please try again with a more specific description.");
          }
        }
      } else {
        setAiError(response.message || 'Failed to generate content. Please try again.');
      }
    } catch (err) {
      setAiError('An error occurred while generating the content. Please try again.');
    } finally {
      setIsGeneratingActivity(false);
    }
  };
  
  // Function to add the AI-generated activity to the plan
  const handleAddGeneratedActivity = async () => {
    if (!generatedActivity) return;
    
    try {
      // Find the day to update
      const dayIndex = parsedData.days.findIndex(day => day.id === selectedDayId);
      if (dayIndex === -1) throw new Error('Day not found');
      
      const dayDate = parsedData.days[dayIndex].date;
      
      // Set default duration based on estimatedDuration or default to 2 hours
      let durationMinutes = 120;
      if (generatedActivity.estimatedDuration) {
        if (generatedActivity.estimatedDuration.includes('hour')) {
          const hoursMatch = generatedActivity.estimatedDuration.match(/(\d+(\.\d+)?)-?(\d+(\.\d+)?)?/);
          if (hoursMatch) {
            // If range like "2-3 hours", take the average
            if (hoursMatch[3]) {
              durationMinutes = Math.round(((parseFloat(hoursMatch[1]) + parseFloat(hoursMatch[3])) / 2) * 60);
            } else {
              durationMinutes = Math.round(parseFloat(hoursMatch[1]) * 60);
            }
          }
        } else if (generatedActivity.estimatedDuration.includes('minute')) {
          const minutesMatch = generatedActivity.estimatedDuration.match(/(\d+)/);
          if (minutesMatch) {
            durationMinutes = parseInt(minutesMatch[1]);
          }
        }
      }
      
      // Get existing activities for this day (including accommodations)
      const existingActivities = parsedData.days[dayIndex].activities;
      
      // Calculate start and end datetime using shared utility (string-based, no Date objects)
      // For attractions and transportation, use calculated times
      let startDateTime, endDateTime;
      
      if (aiActivityType === 'accommodation') {
        // Accommodations use check-in/check-out times, handled separately below
        startDateTime = '';
        endDateTime = '';
      } else {
        const calculated = calculateActivityDateTime(dayDate, existingActivities, durationMinutes);
        startDateTime = calculated.startDateTime;
        endDateTime = calculated.endDateTime;
      }
      
      // Add price information to notes if available
      let notes = generatedActivity.notes || '';
      if (generatedActivity.price && generatedActivity.price.trim() !== '') {
        if (notes) {
          notes += '\n\nPrice: ' + generatedActivity.price;
        } else {
          notes = 'Price: ' + generatedActivity.price;
        }
      }
      
      // Common properties for all activity types
      const commonProps = {
        name: generatedActivity.name,
        nameInEnglish: generatedActivity.nameInEnglish || null,
        description: generatedActivity.description || '',
        notes: notes,
        website: generatedActivity.website,
        links: generatedActivity.links || [],
        defaultCurrency: parsedData?.currency || DEFAULT_CURRENCY
      };
      
      // Create a new activity using unified function (automatically adds Google Maps links, excludes estimatedDuration)
      let newActivity;
      
      if (aiActivityType === 'accommodation') {
        // For accommodation: 
        // 1. ALWAYS use the selected day's date as the start date
        // 2. Only the end date can be different but must be validated
        
        // Extract date prefix (YYYY-MM-DD) from selected day
        const dayPrefix = (dayDate || '').split('T')[0];
        
        // Calculate default end date: current day + 3 days, or last day of trip (whichever is earlier)
        let defaultEndDate;
        
        // Get the last day of the trip
        let lastDayOfTrip = null;
        if (parsedData?.endDate) {
          lastDayOfTrip = safeFormatDate(parsedData.endDate, 'yyyy-MM-dd');
        } else if (parsedData?.days && parsedData.days.length > 0) {
          // Get the last day from the days array
          const lastDay = parsedData.days[parsedData.days.length - 1];
          if (lastDay?.date) {
            lastDayOfTrip = (lastDay.date || '').split('T')[0];
          }
        }
        
        // Calculate current day + 3 days
        const threeDaysLater = addDaysToString(dayPrefix, 3);
        
        // Use the minimum (earlier) of: (current day + 3) or last day of trip
        if (lastDayOfTrip && lastDayOfTrip < threeDaysLater) {
          defaultEndDate = lastDayOfTrip;
          clog("Using last day of trip as default end date:", defaultEndDate);
        } else {
          defaultEndDate = threeDaysLater;
          clog("Using current day + 3 days as default end date:", defaultEndDate);
        }
        
        // Calculate end date for checkout (string-based manipulation)
        let nextDayPrefix;
        
        // If the AI response includes an end date, try to use it but VALIDATE that it's after the start date
        if (generatedActivity.endDate) {
          const endStr = (generatedActivity.endDate || '').split('T')[0];
          // Simple string comparison: YYYY-MM-DD format is naturally sortable
          if (endStr && endStr > dayPrefix) {
            // Also validate it doesn't exceed the trip's last day
            if (lastDayOfTrip && endStr > lastDayOfTrip) {
              clog("AI-provided end date exceeds trip end date, using default");
              nextDayPrefix = defaultEndDate;
            } else {
              nextDayPrefix = endStr;
              clog("Using validated end date from AI:", nextDayPrefix);
            }
          } else {
            clog("AI-provided end date is invalid or not after start date, using default");
            nextDayPrefix = defaultEndDate;
          }
        } else {
          clog("No end date found in AI response, using default");
          nextDayPrefix = defaultEndDate;
        }
        
        // Get check-in and check-out times from AI or use defaults
        const checkInTime = generatedActivity.checkInTime || '14:00';
        const checkOutTime = generatedActivity.checkOutTime || '11:00';
        
        // Extract hours and minutes from check-in/out times
        let checkInHours = 14;
        let checkInMinutes = 0;
        let checkOutHours = 11;
        let checkOutMinutes = 0;
        
        // Parse check-in time if it exists
        if (checkInTime) {
          const checkInMatch = checkInTime.match(/(\d+):(\d+)/);
          if (checkInMatch) {
            checkInHours = parseInt(checkInMatch[1]);
            checkInMinutes = parseInt(checkInMatch[2]);
          }
        }
        
        // Parse check-out time if it exists
        if (checkOutTime) {
          const checkOutMatch = checkOutTime.match(/(\d+):(\d+)/);
          if (checkOutMatch) {
            checkOutHours = parseInt(checkOutMatch[1]);
            checkOutMinutes = parseInt(checkOutMatch[2]);
          }
        }
        
        // Create ISO strings with the appropriate times
        const startDateWithCheckInTime = `${dayPrefix}T${String(checkInHours).padStart(2, '0')}:${String(checkInMinutes).padStart(2, '0')}:00.000Z`;
        const endDateWithCheckOutTime = `${nextDayPrefix}T${String(checkOutHours).padStart(2, '0')}:${String(checkOutMinutes).padStart(2, '0')}:00.000Z`;
        
        newActivity = createNormalizedActivity({
          ...commonProps,
          type: "accommodation",
          startDateTime: startDateWithCheckInTime,
          endDateTime: endDateWithCheckOutTime, 
          checkInTime: checkInTime,
          checkOutTime: checkOutTime,
          location: normalizeLocation(generatedActivity.location),
          images: generatedActivity.images || [] // Add images to accommodations
        }, {
          addGoogleMapsLinks: true,
          includeEstimatedDuration: false
        });
      } else if (aiActivityType === 'transportation') {
        // For transportation, ALWAYS use the selected day's date, ignore any AI dates
        // This ensures transportation is always on the day the user selected
        
        newActivity = createNormalizedActivity({
          ...commonProps,
          type: "transportation",
          startDateTime: startDateTime, // Always use the selected day's date with default noon time
          endDateTime: endDateTime,     // Always use the selected day's date with calculated end time
          fromLocation: normalizeLocation(generatedActivity.fromLocation),
          toLocation: normalizeLocation(generatedActivity.toLocation),
          images: generatedActivity.images || [] // Add images to transportation
        }, {
          addGoogleMapsLinks: true,
          includeEstimatedDuration: false
        });
      } else {
        // For attractions - ALWAYS use the selected day's date, ignore any AI dates
        newActivity = createNormalizedActivity({
          ...commonProps,
          type: "attraction",
          startDateTime: startDateTime, // Always use the selected day's date with default noon time
          endDateTime: endDateTime,     // Always use the selected day's date with calculated end time
          location: normalizeLocation(generatedActivity.location),
          images: generatedActivity.images || []
        }, {
          addGoogleMapsLinks: true,
          includeEstimatedDuration: false
        });
      }
      
      // Create updated data with the new activity
      const updatedData = { ...parsedData };
      updatedData.days[dayIndex].activities.push(newActivity);
      
      // Sort activities by time
      updatedData.days[dayIndex].activities = sortActivitiesByTime(updatedData.days[dayIndex].activities);
      
      // Save changes
      const saved = await saveChanges(updatedData);
      
      if (saved) {
        // Close the AI modal only on successful save
        setShowAiModal(false);
        setGeneratedActivity(null);
        setGeneratedActivities([]);
        setAiPrompt('');
      }
      // Error is already displayed by saveChanges via setError
    } catch (error) {
      cerror('Error adding AI-generated activity:', error);
      // This should not happen now since saveChanges doesn't throw, but keep as safety net
      if (error.message && error.message.includes('VERSION_MISMATCH')) {
        const errorMsg = error.message.replace('VERSION_MISMATCH: ', '');
        setError(errorMsg);
      } else {
        setAiError(`Failed to add the ${aiActivityType} to your plan. Please try again.`);
      }
    }
  };

  // Function to select a specific generated activity
  const handleSelectActivity = (index) => {
    if (index >= 0 && index < generatedActivities.length) {
      setSelectedActivityIndex(index);
      setGeneratedActivity(generatedActivities[index]);
    }
  };

  // Note: Authentication is handled by ProtectedRoute wrapper, so currentUser is guaranteed to exist

  // Render the appropriate modal based on activity type
  const renderActivityModal = () => {
    // For accommodation activities, use the specialized modal
    if (currentActivityType === 'accommodation') {
      return (
        <AccommodationModal
          show={showModal}
          onHide={() => setShowModal(false)}
          activity={selectedActivity}
          onSave={handleSaveActivity}
          dayId={selectedDayId}
          isNew={isNewActivity}
          parsedData={parsedData}
          planLanguage={plan?.language || 'en'}
          onSwitchToAI={() => handleOpenAiModal(selectedDayId, 'accommodation', true)}
          accountSettings={accountSettings}
        />
      );
    }
    
    // For transportation activities, use the specialized modal
    if (currentActivityType === 'transportation') {
      return (
        <TransportationModal
          show={showModal}
          onHide={() => setShowModal(false)}
          activity={selectedActivity}
          onSave={handleSaveActivity}
          dayId={selectedDayId}
          isNew={isNewActivity}
          parsedData={parsedData}
          planLanguage={plan?.language || 'en'}
          onSwitchToAI={() => handleOpenAiModal(selectedDayId, 'transportation', true)}
          accountSettings={accountSettings}
        />
      );
    }
    
    // For attraction activities, use the specialized modal
    if (currentActivityType === 'attraction') {
      return (
        <AttractionModal
          show={showModal}
          onHide={() => setShowModal(false)}
          activity={selectedActivity}
          onSave={handleSaveActivity}
          dayId={selectedDayId}
          isNew={isNewActivity}
          parsedData={parsedData}
          planLanguage={plan?.language || 'en'}
          onSwitchToAI={() => handleOpenAiModal(selectedDayId, 'attraction', true)}
          accountSettings={accountSettings}
        />
      );
    }
  };

  // Replace the existing tab selection handler
  const handleTabChange = (tabKey) => {
    setActiveTab(tabKey);
    
    // Update URL query string to reflect current tab
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('tab', tabKey);
    setSearchParams(newSearchParams, { replace: true });
    
    // Check for new version on tab change
    window.dispatchEvent(new Event('checkVersion'));
  };

  const currentDayIndex = useMemo(
    () => getCurrentDayInPlan(parsedData?.startDate, parsedData?.endDate),
    [parsedData?.startDate, parsedData?.endDate]
  );

  
  const handleAddDayTodo = (dayId, newTodo) => {
    // Update local state
    const updatedDayTodos = {
      ...dayTodos,
      [dayId]: [...(dayTodos[dayId] || []), newTodo]
    };
    setDayTodos(updatedDayTodos);
    
    // Update full data structure
    const updatedData = {
      ...parsedData,
      days: parsedData.days.map(day => {
        if (day.id === dayId) {
          return {
            ...day,
            todos: updatedDayTodos[dayId]
          };
        }
        return day;
      })
    };
    
    saveChanges(updatedData);
  };
  
  const handleChangeDayTodo = (dayId, updatedTodo) => {
    // Update local state
    const updatedDayTodos = {
      ...dayTodos,
      [dayId]: (dayTodos[dayId] || []).map(todo => 
        todo.id === updatedTodo.id ? updatedTodo : todo
      )
    };
    setDayTodos(updatedDayTodos);
    
    // Update full data structure
    const updatedData = {
      ...parsedData,
      days: parsedData.days.map(day => {
        if (day.id === dayId) {
          return {
            ...day,
            todos: updatedDayTodos[dayId]
          };
        }
        return day;
      })
    };
    
    // Add a slight delay before saving to ensure the loading spinner is visible
    setTimeout(() => {
      saveChanges(updatedData);
    }, 400);
  };
  
  const handleEditDayTodo = (dayId, updatedTodo) => {
    // Update local state
    const updatedDayTodos = {
      ...dayTodos,
      [dayId]: (dayTodos[dayId] || []).map(todo => 
        todo.id === updatedTodo.id ? updatedTodo : todo
      )
    };
    setDayTodos(updatedDayTodos);
    
    // Update full data structure
    const updatedData = {
      ...parsedData,
      days: parsedData.days.map(day => {
        if (day.id === dayId) {
          return {
            ...day,
            todos: updatedDayTodos[dayId]
          };
        }
        return day;
      })
    };
    
    saveChanges(updatedData);
  };
  
  const handleDeleteDayTodo = (dayId, todoId) => {
    // Update local state
    const updatedDayTodos = {
      ...dayTodos,
      [dayId]: (dayTodos[dayId] || []).filter(todo => todo.id !== todoId)
    };
    setDayTodos(updatedDayTodos);
    
    // Update full data structure
    const updatedData = {
      ...parsedData,
      days: parsedData.days.map(day => {
        if (day.id === dayId) {
          return {
            ...day,
            todos: updatedDayTodos[dayId]
          };
        }
        return day;
      })
    };
    
    saveChanges(updatedData);
  }; 
 
  
  // Function to copy JSON data to clipboard
  const copyJsonToClipboard = () => {
    const jsonString = JSON.stringify(parsedData, null, 2);
    navigator.clipboard.writeText(jsonString)
      .then(() => {
        // Show temporary success message
        const copyBtn = document.getElementById('copy-json-btn');
        if (copyBtn) {
          const originalText = copyBtn.innerHTML;
          copyBtn.innerHTML = '<i class="bi bi-check"></i> Copied!';
          copyBtn.disabled = true;
          
          setTimeout(() => {
            copyBtn.innerHTML = originalText;
            copyBtn.disabled = false;
          }, 2000);
        }
      })
      .catch(err => {
        cerror('Failed to copy JSON to clipboard:', err);
        alert('Failed to copy to clipboard. Please try again.');
      });
  };
  
  // Helper function to scroll to a day element (assumes we're already on days tab)
  const scrollToDayElement = (dayId, attempt = 0) => {
    const dayElement = document.getElementById(`day-card-${dayId}`);
    if (dayElement) {
      const offset = 100;
      const top = dayElement.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({
        top: Math.max(0, top),
        behavior: 'smooth'
      });
      return;
    }
    if (attempt < 5) {
      setTimeout(() => scrollToDayElement(dayId, attempt + 1), 100);
    }
  };

  const scrollToDay = (dayId) => {
    setSelectedDayId(dayId);
    
    // If not on the days tab, switch to it first
    if (activeTab !== 'days') {
      handleTabChange('days');
      // Wait for tab to render, then scroll to day
      setTimeout(() => {
        scrollToDayElement(dayId);
      }, 100);
    } else {
      // Already on days tab, scroll immediately
      scrollToDayElement(dayId);
    }
  };

  const scrollToCurrentDay = () => {
    if (currentDayIndex < 0 || !parsedData?.days?.[currentDayIndex]) {
      return;
    }
    scrollToDay(parsedData.days[currentDayIndex].id);
  };

  // Function to update activity completion status without opening the edit popup
  const updateActivityCompletionStatus = (activity, dayId) => {
    try {
      // Create a deep copy of the parsed data to ensure all properties are included
      const updatedData = JSON.parse(JSON.stringify(parsedData));
      
      // Find the day to update
      const dayIndex = updatedData.days.findIndex(day => day.id === dayId);
      if (dayIndex === -1) {
        throw new Error('Day not found');
      }
      
      // Find the activity index
      const activityIndex = updatedData.days[dayIndex].activities.findIndex(a => a.id === activity.id);
      if (activityIndex === -1) {
        throw new Error('Activity not found');
      }
      
      // Update the activity with the new completed status
      updatedData.days[dayIndex].activities[activityIndex].completed = activity.completed;
      
      // Save changes to the backend
      saveChanges(updatedData);
      
      // Also update the local state directly to ensure UI reflects changes
      setParsedData(updatedData);
    } catch (error) {
      cerror('Error updating activity completion status:', error);
      setError('Failed to update activity status. Please try again.');
    }
  };

  const updateActivityDescriptionAlign = (activity, dayId) => {
    try {
      const updatedData = JSON.parse(JSON.stringify(parsedData));
      const dayIndex = updatedData.days.findIndex((day) => day.id === dayId);
      if (dayIndex === -1) {
        throw new Error('Day not found');
      }

      const activityIndex = updatedData.days[dayIndex].activities.findIndex((item) => item.id === activity.id);
      if (activityIndex === -1) {
        throw new Error('Activity not found');
      }

      updatedData.days[dayIndex].activities[activityIndex].descriptionAlign = activity.descriptionAlign;
      saveChanges(updatedData);
      setParsedData(updatedData);
    } catch (error) {
      cerror('Error updating activity description alignment:', error);
      setError('Failed to update description alignment. Please try again.');
    }
  };

  return (
    <div className="plan-detail-page">
      <SlidingMenu 
        title={t('planDetail.tripDetails')}
        days={parsedData?.days}
        onDaySelect={scrollToDay}
      />

      <Container className="py-4">
        {successMessage && (
          <Alert variant="success" className="mb-4" onClose={() => setSuccessMessage('')} dismissible>
            <i className="bi bi-check-circle me-2"></i>
            {successMessage}
          </Alert>
        )}
        
        {isLoading ? (
          <div className="text-center py-5">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">{t('common.loading')}</span>
            </Spinner>
            <p className="mt-2">{t('planDetail.loadingTripDetails')}</p>
          </div>
        ) : plan && parsedData ? (
          <>
            {/* Owner banner: fixed 80px height, 80% width, image contained (no clip/distort) */}
            {(plan?.ownerCopiedBanner?.url || plan?.ownerPlanSharedData?.banner?.url) && (
              <div className="mb-3 mx-auto" style={{ width: '80%', height: '80px', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <img
                  src={plan.ownerCopiedBanner?.url || plan.ownerPlanSharedData.banner.url}
                  alt={(plan.ownerCopiedBanner?.name || plan.ownerPlanSharedData?.banner?.name) || 'Banner'}
                  style={{ maxHeight: '100%', maxWidth: '100%', width: 'auto', height: 'auto', objectFit: 'contain' }}
                />
              </div>
            )}
            {/* Updated header section for better mobile responsiveness */}
            <div className="mb-4">
              <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-2">
                <div className="mb-3 mb-md-0 flex-grow-1">
                  <h1 className="mb-1">{parsedData.name}</h1>
                  <div className="d-flex align-items-center w-100">
                    {parsedData.startDate && parsedData.endDate && (
                      <p className="text-muted mb-0 me-3">
                        <span className="d-none d-md-inline">{formatDateWithLocale(parsedData.startDate, 'PPP')} - {formatDateWithLocale(parsedData.endDate, 'PPP')}</span>
                        <span className="d-md-none">{formatDateWithLocale(parsedData.startDate, 'EEE, MMM d')} - {formatDateWithLocale(parsedData.endDate, 'EEE, MMM d')}</span>
                      </p>
                    )}
                    {/* Mobile/Tablet: Dropdown menu - inline with dates, pushed to right (LTR) or left (RTL) */}
                    <div className={`d-xl-none ${isRTL(i18n.language) ? 'me-auto' : 'ms-auto'}`}>
                  <Dropdown align={isRTL(i18n.language) ? 'start' : 'end'}>
                    <Dropdown.Toggle 
                      variant="outline-secondary" 
                      size="sm" 
                      id="plan-actions-dropdown"
                      disabled={isGeneratingPDF}
                    >
                      {isGeneratingPDF ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                            className="me-1"
                          />
                          <span className="d-none d-sm-inline">{t('planDetail.actions.generatingPDF') || 'Generating PDF...'}</span>
                        </>
                      ) : (
                        <i className="bi bi-three-dots-vertical"></i>
                      )}
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      {activeTab === 'days' && (
                        <Dropdown.Item onClick={() => setIsMinimized(!isMinimized)}>
                          <i className={`bi ${isMinimized ? 'bi-arrows-angle-expand' : 'bi-arrows-angle-contract'} me-2`}></i>
                          {isMinimized ? t('planDetail.actions.expandView') : t('planDetail.actions.minimizeView')}
                        </Dropdown.Item>
                      )}
                      {(activeTab === 'overview' || activeTab === 'days') && (
                        <Dropdown.Item onClick={activeTab === 'overview' ? startOverviewTour : startDaysTour}>
                          <i className="bi bi-question-circle me-2"></i>
                          {t('planDetail.actions.helpTour')}
                        </Dropdown.Item>
                      )}
                      <Dropdown.Item onClick={handleSharePlan}>
                        <i className={plan?.sharingData?.externalShare?.shareId ? "bi bi-check-circle me-2" : (canShare ? "bi bi-share me-2" : "bi bi-lock-fill me-2")}></i>
                        {t('planDetail.actions.share')} {!canShare && <Badge bg="warning" className="ms-1">{t('planDetail.actions.pro')}</Badge>}
                        {plan?.sharingData?.externalShare?.shareId && (
                          <Badge bg="success" className="ms-1">{t('planDetail.actions.shared')}</Badge>
                        )}
                      </Dropdown.Item>
                      {canManageGroups && (
                        <Dropdown.Item onClick={handleShareToGroups} disabled={isSharingToGroups}>
                          <i className={`bi ${plan?.sharedWithGroups && plan.sharedWithGroups.length > 0 ? 'bi-check-circle' : 'bi-people'} me-2`}></i>
                          {t('planDetail.actions.shareToGroup')}
                          {plan?.sharedWithGroups && plan.sharedWithGroups.length > 0 && (
                            <Badge bg="success" className="ms-1">{t('planDetail.actions.shared')}</Badge>
                          )}
                        </Dropdown.Item>
                      )}
                      <Dropdown.Divider />
                      <Dropdown.Header>{t('planDetail.actions.export') || 'Export'}</Dropdown.Header>
                      <Dropdown.Item onClick={handleDownloadPDF} disabled={isGeneratingPDF}>
                        {isGeneratingPDF ? (
                          <>
                            <Spinner
                              as="span"
                              animation="border"
                              size="sm"
                              role="status"
                              aria-hidden="true"
                              className="me-2"
                            />
                            {t('planDetail.actions.generatingPDF') || 'Generating PDF...'}
                          </>
                        ) : (
                          <>
                            <i className={canExportPDF ? "bi bi-file-earmark-pdf me-2" : "bi bi-lock-fill me-2"}></i>
                            {t('planDetail.actions.downloadPDF') || 'Download PDF'} {!canExportPDF && <Badge bg="warning" className="ms-1">{t('planDetail.actions.pro')}</Badge>}
                          </>
                        )}
                      </Dropdown.Item>
                      <Dropdown.Item 
                        onClick={() => {
                          if (!canExportPDF) {
                            setUpgradeMessage(t('planDetail.upgrade.pdfExport'));
                            setShowUpgradeModal(true);
                            return;
                          }
                          window.open(`/print/plan/${planId}`, '_blank');
                        }}
                      >
                        <i className={canExportPDF ? "bi bi-printer me-2" : "bi bi-lock-fill me-2"}></i>
                        {t('planDetail.actions.print')} {!canExportPDF && <Badge bg="warning" className="ms-1">{t('planDetail.actions.pro')}</Badge>}
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                    </div>
                  </div>
                </div>
                
                {/* Desktop: Regular buttons */}
                <div id="desktop-actions-buttons" className="d-none d-xl-flex flex-wrap justify-content-end">
                  {activeTab === 'days' && (
                    <Button 
                      id="minimize-view-btn"
                      variant="outline-secondary" 
                      className="me-2 mb-2 mb-md-0"
                      onClick={() => setIsMinimized(!isMinimized)}
                      title={isMinimized ? t('planDetail.actions.expandView') : t('planDetail.actions.minimizeView')}
                      size="sm"
                    >
                      <i className={`bi ${isMinimized ? 'bi-arrows-angle-expand' : 'bi-arrows-angle-contract'}`}></i>
                      <span className="ms-1">{isMinimized ? t('planDetail.actions.expandView') : t('planDetail.actions.minimizeView')}</span>
                    </Button>
                  )}
                  {(activeTab === 'overview' || activeTab === 'days') && (
                    <Button 
                      variant="outline-info" 
                      className="me-2 mb-2 mb-md-0"
                      onClick={activeTab === 'overview' ? startOverviewTour : startDaysTour}
                      size="sm"
                      title={t('planDetail.actions.helpTour')}
                    >
                      <i className="bi bi-question-circle me-1"></i>
                      {t('planDetail.actions.helpTour')}
                    </Button>
                  )}
                  <Button 
                    variant={plan?.sharingData?.externalShare?.shareId ? "success" : (canShare ? "outline-success" : "outline-secondary")}
                    className="me-2 mb-2 mb-md-0"
                    onClick={handleSharePlan}
                    size="sm"
                    title={plan?.sharingData?.externalShare?.shareId ? t('planDetail.actions.planSharedExternally') : (canShare ? t('planDetail.actions.shareYourTrip') : t('planDetail.actions.upgradeToShare'))}
                    id="share-plan-btn"
                  >
                    <i className={plan?.sharingData?.externalShare?.shareId ? "bi bi-check-circle me-1" : (canShare ? "bi bi-share me-1" : "bi bi-lock-fill me-1")}></i>
                    {t('planDetail.actions.share')} {!canShare && <Badge bg="warning" className="ms-1">{t('planDetail.actions.pro')}</Badge>}
                  </Button>
                  {canManageGroups && (
                    <Button 
                      variant={plan?.sharedWithGroups && plan.sharedWithGroups.length > 0 ? "success" : "outline-info"}
                      className="me-2 mb-2 mb-md-0"
                      onClick={handleShareToGroups}
                      disabled={isSharingToGroups}
                      size="sm"
                      title={plan?.sharedWithGroups && plan.sharedWithGroups.length > 0 ? t('planDetail.actions.planSharedWithGroups') : t('planDetail.actions.shareYourTripWithGroups')}
                    >
                      {isSharingToGroups ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                            className="me-1"
                          />
                          {t('common.loading')}
                        </>
                      ) : (
                        <>
                          <i className={`bi ${plan?.sharedWithGroups && plan.sharedWithGroups.length > 0 ? 'bi-check-circle' : 'bi-people'} me-1`}></i>
                          {t('planDetail.actions.shareToGroup')}
                        </>
                      )}
                    </Button>
                  )}
                  <Dropdown>
                    <Dropdown.Toggle 
                      variant={canExportPDF ? "outline-primary" : "outline-secondary"}
                      className="me-2 mb-2 mb-md-0"
                      size="sm"
                      title={canExportPDF ? t('planDetail.actions.printOrExport') : t('planDetail.actions.upgradeToExport')}
                      disabled={isGeneratingPDF}
                    >
                      {isGeneratingPDF ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                            className="me-1"
                          />
                          {t('planDetail.actions.generatingPDF') || 'Generating PDF...'}
                        </>
                      ) : (
                        <>
                          <i className={canExportPDF ? "bi bi-printer me-1" : "bi bi-lock-fill me-1"}></i>
                          {t('planDetail.actions.print')} {!canExportPDF && <Badge bg="warning" className="ms-1">{t('planDetail.actions.pro')}</Badge>}
                        </>
                      )}
                    </Dropdown.Toggle>
                    <Dropdown.Menu align={isRTL(i18n.language) ? 'start' : 'end'}>
                      <Dropdown.Header>{t('planDetail.actions.export') || 'Export'}</Dropdown.Header>
                      <Dropdown.Item onClick={handleDownloadPDF} disabled={isGeneratingPDF}>
                        {isGeneratingPDF ? (
                          <>
                            <Spinner
                              as="span"
                              animation="border"
                              size="sm"
                              role="status"
                              aria-hidden="true"
                              className="me-2"
                            />
                            {t('planDetail.actions.generatingPDF') || 'Generating PDF...'}
                          </>
                        ) : (
                          <>
                            <i className={canExportPDF ? "bi bi-file-earmark-pdf me-2" : "bi bi-lock-fill me-2"}></i>
                            {t('planDetail.actions.downloadPDF') || 'Download PDF'} {!canExportPDF && <Badge bg="warning" className="ms-1">{t('planDetail.actions.pro')}</Badge>}
                          </>
                        )}
                      </Dropdown.Item>
                      <Dropdown.Item 
                        onClick={() => {
                          if (!canExportPDF) {
                            setUpgradeMessage(t('planDetail.upgrade.pdfExport'));
                            setShowUpgradeModal(true);
                            return;
                          }
                          window.open(`/print/plan/${planId}`, '_blank');
                        }}
                      >
                        <i className={canExportPDF ? "bi bi-printer me-2" : "bi bi-lock-fill me-2"}></i>
                        {t('planDetail.actions.print')} {!canExportPDF && <Badge bg="warning" className="ms-1">{t('planDetail.actions.pro')}</Badge>}
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
              </div>
            </div>
            
            <ScrollableTabs
              tabs={[
                { eventKey: 'overview', title: t('planDetail.tabs.overview') },
                { eventKey: 'days', title: t('planDetail.tabs.days') },
                { eventKey: 'locations', title: t('planDetail.tabs.locations') },
                { eventKey: 'main-locations', title: t('planDetail.tabs.mainLocations') },
                { eventKey: 'costs', title: t('planDetail.tabs.costs') },
                { eventKey: 'weather', title: t('planDetail.tabs.weather') },
                { eventKey: 'info', title: t('planDetail.tabs.info') },
                { eventKey: 'transportation-info', title: t('planDetail.tabs.transportation') },
                { eventKey: 'packing', title: t('planDetail.tabs.packing') },
                ...(debugMode && isAdmin ? [{ eventKey: 'rawData', title: t('planDetail.tabs.rawData') }] : [])
              ]}
              activeTab={activeTab}
              onTabChange={handleTabChange}
            >
              <ScrollableTabs.Tab eventKey="overview">
                <OverviewTab 
                  parsedData={parsedData} 
                  plan={plan} 
                  planId={planId}
                  onSaveChanges={saveChanges} 
                  activeTab={activeTab}
                />
              </ScrollableTabs.Tab>
              
              <ScrollableTabs.Tab eventKey="days">
                {currentDayIndex >= 0 && (
                  <div className="mb-3">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={scrollToCurrentDay}
                    >
                      <i className="bi bi-calendar-check me-1"></i>
                      {t('planDetail.days.scrollToCurrentDay')}
                    </Button>
                  </div>
                )}
                {isMinimized ? (
                  <MinimizedDaysViewSimple
                    days={parsedData.days}
                  />
                ) : parsedData.days && parsedData.days.length > 0 ? (
                  parsedData.days.map((day, index) => {
                    // Get any multi-day accommodations
                    const multiDayAccoms = findMultiDayAccommodations(parsedData);
                    const continueAccoms = multiDayAccoms.filter(accom => 
                      isDayWithinAccommodationRange(day.date, accom.startDateTime, accom.endDateTime) &&
                      accom.originalDay !== day.date
                    );
                    const nightlyAccoms = (day.activities || []).filter(activity =>
                      activity.type === 'accommodation' &&
                      getDatePart(activity.startDateTime) === day.date
                    );
                    const isHotelChangeDay = continueAccoms.length > 0 && nightlyAccoms.length > 0;
                    const isPlanReady = day.planReady === true;
                    const isDayCollapsed = isDayCollapsedByDefault(day.id, isPlanReady);
                    
                    // Return full view
                    return (
                      <Card
                        id={`day-card-${day.id}`}
                        key={day.id || index}
                        className={`mb-4 shadow-sm ${selectedDayId === day.id ? 'border-primary' : ''}`}
                      >
                        <Card.Header className="bg-light px-2 px-md-3">
                          <div className="d-flex justify-content-between align-items-center">
                            <div className="d-flex align-items-center">
                              <h5 className="mb-0 d-none d-md-block">
                                <i className="bi bi-calendar-date me-2"></i>
                                {t('planDetail.days.day')} {index + 1}: {day.date ? formatDateWithLocale(day.date, 'EEEE, PPP') : ''}
                              </h5>
                              <h6 className="mb-0 d-md-none">
                                <i className="bi bi-calendar-date me-1"></i>
                                {t('planDetail.days.day')} {index + 1}: {day.date ? formatDateWithLocale(day.date, 'EEE, MMM d') : ''}
                              </h6>
                              {/* Show multiple options indicator if day has multiple options */}
                              {hasMultipleOptions(day) && (
                                <Badge bg="info" className="ms-2" style={{ fontSize: '0.75rem' }}>
                                  {t('planDetail.days.multipleOptions')}
                                </Badge>
                              )}
                              {isPlanReady && (
                                <i
                                  className="bi bi-circle-fill text-success ms-2"
                                  title={t('planDetail.days.planReady', { defaultValue: 'Plan ready' })}
                                  aria-label={t('planDetail.days.planReady', { defaultValue: 'Plan ready' })}
                                  style={{ fontSize: '0.7rem' }}
                                ></i>
                              )}
                            </div>
                            <div className="d-flex align-items-center gap-2">
                              {/* Collapse/Expand All Activities Button */}
                              {day.activities && day.activities.length > 0 && (
                                <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  onClick={() => toggleDayActivities(day.id, isPlanReady)}
                                  title={isDayCollapsed ? t('planDetail.days.expandAllActivities') : t('planDetail.days.collapseAllActivities')}
                                >
                                  <i className={`bi ${isDayCollapsed ? 'bi-chevron-down' : 'bi-chevron-up'}`}></i>
                                </Button>
                              )}
                              <Dropdown>
                              <Dropdown.Toggle 
                                variant="primary" 
                                id={`dropdown-day-actions-${day.id}`}
                                className={index === 0 ? 'first-day-actions-btn' : ''}
                              >
                                <i className="bi bi-gear"></i>
                                <span className="d-none d-sm-inline ms-1">{t('planDetail.days.dayActions')}</span>
                              </Dropdown.Toggle>
                              <Dropdown.Menu align={isRTL(i18n.language) ? 'start' : 'end'}>
                                <Dropdown.Header>{t('planDetail.days.generateWithAI')}</Dropdown.Header>
                                <Dropdown.Item 
                                  onClick={() => handleOpenAiModal(day.id)}
                                  className="fw-bold dropdown-item-prominent"
                                  style={{ color: '#0d6efd' }}
                                >
                                  <i className="bi bi-magic me-2"></i>
                                  {t('planDetail.days.addActivity')}
                                </Dropdown.Item>
                                <Dropdown.Divider />
                                <Dropdown.Header>{t('planDetail.days.addSuggestions')}</Dropdown.Header>
                                <Dropdown.Item 
                                  onClick={() => handleOpenSuggestions(day.id)}
                                  className="fw-bold dropdown-item-prominent"
                                  style={{ color: '#0d6efd' }}
                                >
                                  <i className="bi bi-lightbulb me-2"></i>
                                  {t('planDetail.days.viewSuggestedActivities')}
                                </Dropdown.Item>
                                <Dropdown.Divider />
                                <Dropdown.Item onClick={() => handleOpenDayMap(day, index)}>
                                  <i className="bi bi-map me-2"></i>
                                  {t('planDetail.days.map')}
                                </Dropdown.Item>
                                <Dropdown.Item onClick={() => handleOpenDayShortsMap(day, index)}>
                                  <i className="bi bi-camera-video me-2"></i>
                                  {t('planDetail.days.shortsMap.menuItem')}
                                </Dropdown.Item>
                                <Dropdown.Item onClick={() => handleOpenDayShortsStats(day, index)}>
                                  <i className="bi bi-bar-chart-line me-2"></i>
                                  {t('planDetail.days.shortsStats.menuItem')}
                                </Dropdown.Item>
                                <Dropdown.Item
                                  onClick={() => {
                                    setSwapDaysSourceDayId(day.id);
                                    setShowSwapDaysModal(true);
                                  }}
                                >
                                  <i className="bi bi-arrow-left-right me-2"></i>
                                  {t('planDetail.days.swapDays.title', { defaultValue: 'Swap days' })}
                                </Dropdown.Item>
                                <Dropdown.Item
                                  onClick={() => {
                                    setDaySummaryDayId(day.id);
                                    setShowDaySummaryModal(true);
                                  }}
                                >
                                  <i className="bi bi-card-text me-2"></i>
                                  {t('planDetail.days.daySummary.title', { defaultValue: 'Day summary' })}
                                </Dropdown.Item>
                                <Dropdown.Item onClick={() => handleCopyActivitiesToChat(day, index)}>
                                  <i className="bi bi-chat-dots me-2"></i>
                                  {t('planDetail.days.copyActivitiesToChat.menuItem')}
                                </Dropdown.Item>
                                <Dropdown.Divider />
                                <Dropdown.Header>{t('planDetail.days.addManually')}</Dropdown.Header>
                                <Dropdown.Item 
                                  onClick={() => handleAddActivityWithType(day.id, 'accommodation')}
                                >
                                  <i className="bi bi-house-door me-2"></i>
                                  {t('planDetail.days.addAccommodation')}
                                </Dropdown.Item>
                                <Dropdown.Item 
                                  onClick={() => handleAddActivityWithType(day.id, 'transportation')}
                                >
                                  <span className="route-icon me-2"></span>
                                  {t('planDetail.days.addTransportation')}
                                </Dropdown.Item>
                                <Dropdown.Item 
                                  onClick={() => handleAddActivityWithType(day.id, 'attraction')}
                                >
                                  <i className="bi bi-geo-alt me-2"></i>
                                  {t('planDetail.days.addAttraction')}
                                </Dropdown.Item>
                                <Dropdown.Divider />
                                <Dropdown.Item
                                  onClick={() => toggleDayPlanReady(day.id)}
                                >
                                  <i className={`bi ${isPlanReady ? 'bi-check-circle-fill text-success' : 'bi-circle'} me-2`}></i>
                                  {isPlanReady
                                    ? t('planDetail.days.markAsNotReady', { defaultValue: 'Mark as not ready' })
                                    : t('planDetail.days.markAsReady', { defaultValue: 'Mark as plan ready' })}
                                </Dropdown.Item>
                                <Dropdown.Divider />
                                <Dropdown.Header>{t('planDetail.days.filterByOption')}</Dropdown.Header>
                                <Dropdown.Item
                                  onClick={() => setDayOptionFilters(prev => ({ ...prev, [day.id]: 'all' }))}
                                >
                                  <i 
                                    className="bi bi-list-ul me-2" 
                                    style={{
                                      backgroundColor: dayOptionFilters[day.id] === 'all' ? '#0d6efd' : 'transparent',
                                      color: dayOptionFilters[day.id] === 'all' ? '#fff' : 'inherit',
                                      borderRadius: '50%',
                                      width: '20px',
                                      height: '20px',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}
                                  ></i>
                                  {t('planDetail.days.allOptions')}
                                </Dropdown.Item>
                                <Dropdown.Item
                                  onClick={() => setDayOptionFilters(prev => ({ ...prev, [day.id]: 1 }))}
                                >
                                  <i 
                                    className="bi bi-1-circle me-2" 
                                    style={{
                                      backgroundColor: (dayOptionFilters[day.id] === 1 || dayOptionFilters[day.id] === undefined) ? '#0d6efd' : 'transparent',
                                      color: (dayOptionFilters[day.id] === 1 || dayOptionFilters[day.id] === undefined) ? '#fff' : 'inherit',
                                      borderRadius: '50%',
                                      width: '20px',
                                      height: '20px',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}
                                  ></i>
                                  {t('planDetail.days.option')} 1
                                </Dropdown.Item>
                                <Dropdown.Item
                                  onClick={() => setDayOptionFilters(prev => ({ ...prev, [day.id]: 2 }))}
                                >
                                  <i 
                                    className="bi bi-2-circle me-2" 
                                    style={{
                                      backgroundColor: dayOptionFilters[day.id] === 2 ? '#0d6efd' : 'transparent',
                                      color: dayOptionFilters[day.id] === 2 ? '#fff' : 'inherit',
                                      borderRadius: '50%',
                                      width: '20px',
                                      height: '20px',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}
                                  ></i>
                                  {t('planDetail.days.option')} 2
                                </Dropdown.Item>
                                <Dropdown.Item
                                  onClick={() => setDayOptionFilters(prev => ({ ...prev, [day.id]: 3 }))}
                                >
                                  <i 
                                    className="bi bi-3-circle me-2" 
                                    style={{
                                      backgroundColor: dayOptionFilters[day.id] === 3 ? '#0d6efd' : 'transparent',
                                      color: dayOptionFilters[day.id] === 3 ? '#fff' : 'inherit',
                                      borderRadius: '50%',
                                      width: '20px',
                                      height: '20px',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}
                                  ></i>
                                  {t('planDetail.days.option')} 3
                                </Dropdown.Item>
                                {accountSettings.useGooglePhotos && isDayInPast(day.date) && (
                                  <>
                                    <Dropdown.Divider />
                                    <Dropdown.Item 
                                      href={getGooglePhotosUrl(day.date)}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      as="a"
                                    >
                                      <i className="bi bi-images me-2"></i>
                                      {t('planDetail.days.viewGooglePhotos')}
                                    </Dropdown.Item>
                                  </>
                                )}
                              </Dropdown.Menu>
                            </Dropdown>
                            </div>
                          </div>
                        </Card.Header>
                        <Card.Body className="px-2 px-md-3">
                          {/* Display accommodation status at the top */}
                          {(continueAccoms.length > 0 || nightlyAccoms.length > 0) && (
                            <div className="mb-3">
                              {continueAccoms.map((accom, idx) => (
                                <div key={`continued-${idx}`}>
                                  {renderAccommodationStatusCard(
                                    accom,
                                    isHotelChangeDay
                                      ? t('planDetail.days.stayingAtLeaving', { defaultValue: 'Staying at (leaving)' })
                                      : t('planDetail.days.stayingAt')
                                  )}
                                </div>
                              ))}
                              {nightlyAccoms.map((accom, idx) => (
                                <div key={`nightly-${idx}`}>
                                  {renderAccommodationStatusCard(
                                    accom,
                                    isHotelChangeDay
                                      ? t('planDetail.days.stayingAtNight', { defaultValue: 'Staying at (night)' })
                                      : t('planDetail.days.stayingAt')
                                  )}
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Day summary: collapsible title row + summary text */}
                          {(day.summaryTitle?.trim() || day.summary?.trim()) && (
                            <div className="mb-3">
                              <Card className="border-0 bg-light shadow-sm">
                                <div
                                  className="d-flex align-items-center gap-2 py-2 px-3"
                                  onClick={() => toggleDaySummarySection(day.id)}
                                  style={{ cursor: 'pointer' }}
                                  role="button"
                                  aria-expanded={isDaySummarySectionExpanded(day)}
                                >
                                  <i className={`bi ${isDaySummarySectionExpanded(day) ? 'bi-chevron-down' : 'bi-chevron-right'} flex-shrink-0`}></i>
                                  <h6 className="mb-0 fw-semibold text-dark flex-grow-1 min-w-0">
                                    {day.summaryTitle?.trim() || t('planDetail.days.daySummary.title', { defaultValue: 'Day summary' })}
                                  </h6>
                                  <Button
                                    variant="link"
                                    size="sm"
                                    className="p-0 text-muted flex-shrink-0"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setDaySummaryDayId(day.id);
                                      setShowDaySummaryModal(true);
                                    }}
                                  >
                                    {t('planDetail.days.daySummary.edit', { defaultValue: 'Edit' })}
                                  </Button>
                                </div>
                                {isDaySummarySectionExpanded(day) && day.summary?.trim() && (
                                  <Card.Body className="pt-0 pb-2 px-3">
                                    <p className="mb-0 text-muted small w-100" style={{ whiteSpace: 'pre-wrap' }}>{day.summary.trim()}</p>
                                  </Card.Body>
                                )}
                              </Card>
                            </div>
                          )}

                          {(() => {
                            // Get the selected option filter for this day (default to 1)
                            const selectedOption = dayOptionFilters[day.id] !== undefined ? dayOptionFilters[day.id] : 1;
                            
                            // Filter activities based on selected option
                            const filteredActivities = day.activities ? day.activities.filter(activity => 
                              activityMatchesOption(activity, selectedOption)
                            ) : [];
                            
                            // Check if there are any activities at all (not just filtered)
                            const hasAnyActivities = day.activities && day.activities.length > 0;
                            
                            if (filteredActivities.length > 0) {
                              return filteredActivities.map((activity, actIndex) => (
                                <div
                                  key={activity.id || `act-${actIndex}`}
                                >
                                  {renderActivityCard(activity, day.id, isPlanReady)}
                                </div>
                              ));
                            } else if (hasAnyActivities) {
                              // Has activities but none match the filter
                              return (
                                <Alert variant="info" className="text-center py-4">
                                  <i className="bi bi-funnel display-4 d-block mb-3 text-muted"></i>
                                  <p className="mb-0">{t('planDetail.days.noActivitiesMatchFilter')}</p>
                                </Alert>
                              );
                            } else if (continueAccoms.length === 0) {
                              // No activities at all
                              return (
                                <Alert variant="light" className="text-center py-4">
                                  <i className="bi bi-calendar2-x display-4 d-block mb-3 text-muted"></i>
                                  <p className="mb-3">{t('planDetail.days.noActivitiesPlanned')}</p>
                                  <Button 
                                    variant="primary" 
                                    size="sm"
                                    onClick={() => handleOpenAiModal(day.id)}
                                  >
                                    <i className="bi bi-plus-circle me-1"></i>
                                    {t('planDetail.days.addActivity')}
                                  </Button>
                                </Alert>
                              );
                            }
                            return null;
                          })()}
                          {/* Day-specific TODO list */}
                          <div className="mt-3">
                            <Card className="border-0">
                              <div 
                                className="d-flex justify-content-between align-items-center py-2 px-2 px-md-3 bg-light rounded cursor-pointer"
                                onClick={() => toggleTodoSection(day.id)}
                                style={{ cursor: 'pointer' }}
                              >
                                <h6 className="mb-0">
                                  <i className={`bi ${isTodoSectionExpanded(day.id) ? 'bi-chevron-down' : 'bi-chevron-right'} me-2`}></i>
                                  {t('planDetail.days.dayTodoList', { day: index + 1 })}
                                  {dayTodos[day.id]?.length > 0 && (
                                    <Badge bg="primary" pill className="ms-2">{dayTodos[day.id].length}</Badge>
                                  )}
                                </h6>
                                {/* Removed the "Add" button from here */}
                              </div>
                              {isTodoSectionExpanded(day.id) && (
                                <Card.Body className="pt-3 mt-2 px-2 px-md-3">
                                  <TodoList
                                    todos={dayTodos[day.id] || []}
                                    onTodoChange={(updatedTodo) => handleChangeDayTodo(day.id, updatedTodo)}
                                    onTodoDelete={(todoId) => handleDeleteDayTodo(day.id, todoId)}
                                    onTodoAdd={(newTodo) => handleAddDayTodo(day.id, newTodo)}
                                    onTodoEdit={(updatedTodo) => handleEditDayTodo(day.id, updatedTodo)}
                                    title={t('planDetail.days.dayTodoList', { day: index + 1 })}
                                    source="day"
                                    hideHeader={true}
                                  />
                                </Card.Body>
                              )}
                            </Card>
                          </div>
                        </Card.Body>
                      </Card>
                    );
                  })
                ) : (
                  <Alert variant="info">
                    {t('planDetail.days.noDaysAdded')}
                  </Alert>
                )}
              </ScrollableTabs.Tab>
              
              <ScrollableTabs.Tab eventKey="locations">
                <LocationsTab 
                  parsedData={parsedData}
                  planId={planId}
                  isActive={activeTab === 'locations'}
                />
              </ScrollableTabs.Tab>
              
              <ScrollableTabs.Tab eventKey="main-locations">
                <MainLocationsTab 
                  parsedData={parsedData}
                />
              </ScrollableTabs.Tab>
              
              <ScrollableTabs.Tab eventKey="costs">
                <CostsTab 
                  parsedData={parsedData} 
                  isActive={activeTab === 'costs'}
                  onUpdatePlan={saveChanges}
                />
              </ScrollableTabs.Tab>
              
              <ScrollableTabs.Tab eventKey="weather">
                <WeatherTab parsedData={parsedData} isActive={activeTab === 'weather'} />
              </ScrollableTabs.Tab>

              <ScrollableTabs.Tab eventKey="info">
                <InfoTab parsedData={parsedData} onUpdatePlan={saveChanges} />
              </ScrollableTabs.Tab>
              
              <ScrollableTabs.Tab eventKey="transportation-info">
                <TransportationInfoTab segments={transportationSegments.segments} />
              </ScrollableTabs.Tab>
              
              <ScrollableTabs.Tab eventKey="packing">
                <PackingTab 
                  parsedData={parsedData}
                  onSaveChanges={saveChanges}
                  isActive={activeTab === 'packing'}
                />
              </ScrollableTabs.Tab>
              
              {debugMode && isAdmin && (
                <ScrollableTabs.Tab eventKey="rawData">
                  <Card>
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h4 className="mb-0">Raw Plan Data</h4>
                        <Button 
                          variant="outline-primary" 
                          size="sm"
                          onClick={copyJsonToClipboard}
                          id="copy-json-btn"
                        >
                          <i className="bi bi-clipboard me-1"></i>
                          Copy to Clipboard
                        </Button>
                      </div>
                      <pre className="bg-light p-3" style={{ maxHeight: '500px', overflow: 'auto' }}>
                        {JSON.stringify(parsedData, null, 2)}
                      </pre>
                    </Card.Body>
                  </Card>
                </ScrollableTabs.Tab>
              )}
            </ScrollableTabs>
            
            {/* Use the renderActivityModal function instead of directly rendering ActivityModal */}
            {renderActivityModal()}
            
            {/* Delete Confirmation Modal */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
              <Modal.Header closeButton>
                <Modal.Title>{t('planDetail.days.deleteActivity.title')}</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                {t('planDetail.days.deleteActivity.confirm')}
                {activityToDelete && (
                  <p className="mt-2 mb-0">
                    <strong>{activityToDelete.name}</strong>
                  </p>
                )}
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>{t('planDetail.days.deleteActivity.cancel')}</Button>
                <Button variant="danger" onClick={handleDeleteActivity}>{t('planDetail.days.deleteActivity.delete')}</Button>
              </Modal.Footer>
            </Modal>
            
            {/* Suggested Attractions Modal */}
            <SuggestedAttractionsModal 
              show={showSuggestionsModal}
              onClose={() => setShowSuggestionsModal(false)}
              suggestedAttractions={suggestedAttractions}
              isLoading={isLoadingSuggestions}
              onAddAttraction={handleAddSuggestedAttraction}
              isAttractionAdded={isAttractionAlreadyAdded}
              onRegenerateAttractions={handleRegenerateSuggestions}
            />
            
            {/* Day Map Modal */}
            <DayMapModal
              show={showDayMapModal}
              onHide={() => setShowDayMapModal(false)}
              day={selectedDayForMap}
              dayIndex={selectedDayIndexForMap}
              continuingAccommodations={continuingAccommodationsForMap}
              planId={planId}
            />

            <DayShortsMapModal
              show={showDayShortsMapModal}
              onHide={() => {
                setShowDayShortsMapModal(false);
                setSelectedDayForShortsMap(null);
                setSelectedDayIndexForShortsMap(null);
              }}
              day={selectedDayForShortsMap}
              dayIndex={selectedDayIndexForShortsMap ?? 0}
              planName={parsedData?.name}
              parsedData={parsedData}
              selectedOption={
                selectedDayForShortsMap && dayOptionFilters[selectedDayForShortsMap.id] !== undefined
                  ? dayOptionFilters[selectedDayForShortsMap.id]
                  : 1
              }
            />

            <DayShortsStatsModal
              show={showDayShortsStatsModal}
              onHide={() => {
                setShowDayShortsStatsModal(false);
                setSelectedDayForShortsStats(null);
                setSelectedDayIndexForShortsStats(null);
              }}
              day={
                selectedDayForShortsStats
                  ? parsedData?.days?.find((d) => d.id === selectedDayForShortsStats.id) ?? selectedDayForShortsStats
                  : null
              }
              dayIndex={selectedDayIndexForShortsStats ?? 0}
              planName={parsedData?.name}
              allDays={parsedData?.days || []}
              onSaveStats={handleSaveDayShortsStats}
            />

            <SwapDaysModal
              show={showSwapDaysModal}
              onHide={() => { setShowSwapDaysModal(false); setSwapDaysSourceDayId(null); }}
              days={parsedData?.days || []}
              currentDayId={swapDaysSourceDayId}
              onConfirm={handleSwapDays}
            />

            <DaySummaryModal
              show={showDaySummaryModal}
              onHide={() => { setShowDaySummaryModal(false); setDaySummaryDayId(null); }}
              dayLabel={parsedData?.days?.find((d) => d.id === daySummaryDayId) ? `${t('planDetail.days.day', { defaultValue: 'Day' })} ${parsedData.days.findIndex((d) => d.id === daySummaryDayId) + 1}: ${parsedData.days.find((d) => d.id === daySummaryDayId)?.date ? formatDateWithLocale(parsedData.days.find((d) => d.id === daySummaryDayId).date, 'EEE, MMM d') : ''}` : ''}
              initialSummaryTitle={parsedData?.days?.find((d) => d.id === daySummaryDayId)?.summaryTitle ?? ''}
              initialSummary={parsedData?.days?.find((d) => d.id === daySummaryDayId)?.summary ?? ''}
              onSave={(payload) => handleSaveDaySummary(daySummaryDayId, payload)}
            />

            <Modal
              show={showGeminiNavigateModal}
              onHide={() => setShowGeminiNavigateModal(false)}
              centered
            >
              <Modal.Header closeButton>
                <Modal.Title>
                  <i className="bi bi-clipboard-check text-success me-2"></i>
                  {t('planDetail.days.copyActivitiesToChat.openGeminiTitle')}
                </Modal.Title>
              </Modal.Header>
              <Modal.Body className="text-center py-4">
                <p className="mb-0 fs-5">{t('planDetail.days.copyActivitiesToChat.openGeminiMessage')}</p>
              </Modal.Body>
              <Modal.Footer className="justify-content-center">
                <Button
                  variant="primary"
                  onClick={() => {
                    setShowGeminiNavigateModal(false);
                    window.open('https://gemini.google.com/app', '_blank', 'noopener,noreferrer');
                  }}
                >
                  <i className="bi bi-box-arrow-up-right me-2"></i>
                  {t('planDetail.days.copyActivitiesToChat.openGemini')}
                </Button>
                <Button variant="outline-secondary" onClick={() => setShowGeminiNavigateModal(false)}>
                  {t('planDetail.days.copyActivitiesToChat.stayHere')}
                </Button>
              </Modal.Footer>
            </Modal>

            {/* Upgrade Required Modal */}
            <Modal 
              show={showUpgradeModal} 
              onHide={() => setShowUpgradeModal(false)}
              centered
            >
              <Modal.Header closeButton>
                <Modal.Title>
                  <i className="bi bi-lock-fill text-warning me-2"></i>
                  {t('planDetail.upgrade.proFeatureRequired')}
                </Modal.Title>
              </Modal.Header>
              <Modal.Body className="text-center py-4">
                <p className="mb-0 fs-5">{upgradeMessage}</p>
              </Modal.Body>
              <Modal.Footer className="justify-content-center">
                <Button 
                  variant="primary" 
                  onClick={() => {
                    setShowUpgradeModal(false);
                    navigate('/pricing');
                  }}
                >
                  <i className="bi bi-star-fill me-2"></i>{t('planDetail.upgrade.viewPricing')}
                </Button>
                <Button 
                  variant="outline-secondary" 
                  onClick={() => setShowUpgradeModal(false)}
                >
                  {t('planDetail.upgrade.maybeLater')}
                </Button>
              </Modal.Footer>
            </Modal>

            {/* Share Options Modal */}
            <ShareOptionsModal
              show={showShareOptionsModal}
              onHide={() => setShowShareOptionsModal(false)}
              parsedData={parsedData}
              planId={planId}
              plan={plan}
              onPlanUpdate={handlePlanUpdate}
              exclusions={shareExclusions}
              setExclusions={setShareExclusions}
              expandedSections={expandedSections}
              setExpandedSections={setExpandedSections}
              accountSettings={accountSettings}
            />

            {/* Share to Group Modal */}
            <ShareToGroupModal
              show={showShareToGroupModal}
              onHide={() => setShowShareToGroupModal(false)}
              parsedData={parsedData}
              exclusions={shareToGroupExclusions}
              setExclusions={setShareToGroupExclusions}
              expandedSections={shareToGroupExpandedSections}
              setExpandedSections={setShareToGroupExpandedSections}
              selectedGroupIds={selectedGroupIds}
              setSelectedGroupIds={setSelectedGroupIds}
              shareOptions={shareToGroupOptions}
              setShareOptions={setShareToGroupOptions}
              emailNotification={shareToGroupEmailNotification}
              setEmailNotification={setShareToGroupEmailNotification}
              onProceed={handleProceedWithShareToGroups}
              isSharing={isSharingToGroups}
              message={shareToGroupMessage}
              onClearMessage={() => setShareToGroupMessage({ type: null, text: null })}
              accountSettings={accountSettings}
            />

            {/* Alert Modal */}
            <AlertModal
              show={showErrorModal && !!error}
              onHide={() => {
                setError('');
                setShowErrorModal(false);
                setAlertVariant('error'); // Reset to default
              }}
              message={error}
              variant={alertVariant}
              showUpgradeButton={error?.includes('Pro feature') || error?.includes('Upgrade to Pro')}
            />
            
            {/* AI Activity Generation Modal */}
            <Modal
              show={showAiModal}
              onHide={() => setShowAiModal(false)}
              centered
              size="lg"
            >
              <Modal.Header closeButton>
                <Modal.Title>{t('planDetail.aiActivity.title')}</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                {generatedActivities.length === 0 ? (
                  <>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-bold d-block mb-2">{t('planDetail.aiActivity.activityType')}</Form.Label>
                      <div className="btn-group" role="group">
                        <Button
                          variant={aiActivityType === 'attraction' ? 'primary' : 'outline-primary'}
                          onClick={() => setAiActivityType('attraction')}
                        >
                          <i className="bi bi-geo-alt me-1"></i>
                          {t('planDetail.aiActivity.attraction')}
                        </Button>
                        <Button
                          variant={aiActivityType === 'accommodation' ? 'primary' : 'outline-primary'}
                          onClick={() => setAiActivityType('accommodation')}
                        >
                          <i className="bi bi-house-door me-1"></i>
                          {t('planDetail.aiActivity.hotel')}
                        </Button>
                        <Button
                          variant={aiActivityType === 'transportation' ? 'primary' : 'outline-primary'}
                          onClick={() => setAiActivityType('transportation')}
                        >
                          <span className="route-icon me-1"></span>
                          {t('planDetail.aiActivity.transport')}
                        </Button>
                      </div>
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-bold">
                        {aiActivityType === 'accommodation' ? t('planDetail.aiActivity.prompt.accommodation') :
                         aiActivityType === 'transportation' ? t('planDetail.aiActivity.prompt.transportation') :
                         t('planDetail.aiActivity.prompt.attraction')}
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={5}
                        value={aiPrompt}
                        onChange={(e) => setAiPrompt(e.target.value)}
                        placeholder={
                          aiActivityType === 'accommodation' ? 
                            t('planDetail.aiActivity.placeholder.accommodation') :
                          aiActivityType === 'transportation' ? 
                            t('planDetail.aiActivity.placeholder.transportation') :
                            t('planDetail.aiActivity.placeholder.attraction')
                        }
                      />
                    </Form.Group>
                    {aiError && (
                      <Alert variant="danger" className="mt-3">
                        {aiError}
                      </Alert>
                    )}
                    <Form.Group className="mb-3">
                      <div className="d-flex align-items-center">
                        <Form.Check
                          type="switch"
                          id="use-trip-context-switch"
                          label={t('planDetail.aiActivity.useTripContext')}
                          checked={useTripContext}
                          onChange={(e) => setUseTripContext(e.target.checked)}
                          className="me-2"
                        />
                        <i
                          className="bi bi-question-circle-fill text-primary"
                          style={{ cursor: 'help', fontSize: '1.2rem', opacity: 1, color: '#0d6efd' }}
                          data-tooltip-id="use-trip-context-tooltip"
                          data-tooltip-html={t('planDetail.aiActivity.useTripContextTooltip')}
                        ></i>
                        <Tooltip id="use-trip-context-tooltip" place="right" className="activity-modal-tooltip" />
                      </div>
                    </Form.Group>
                    <div className="d-flex justify-content-end mt-3">
                      <Button 
                        variant="primary" 
                        onClick={handleGenerateActivity}
                        disabled={isGeneratingActivity}
                      >
                        {isGeneratingActivity ? (
                          <>
                            <Spinner
                              as="span"
                              animation="border"
                              size="sm"
                              role="status"
                              aria-hidden="true"
                              className="me-2"
                            />
                            {t('planDetail.aiActivity.generating')}
                          </>
                        ) : (
                          t('planDetail.aiActivity.generateActivities')
                        )}
                      </Button>
                    </div>
                  </>
                ) : (
                  <div>
                    {aiError && (
                      <Alert variant="danger" className="mb-3">
                        {aiError}
                      </Alert>
                    )}
                    
                    <p className="mb-3">{t('planDetail.aiActivity.selectActivity')}</p>
                    
                    <Nav variant="tabs" className="mb-3">
                      {generatedActivities.map((activity, index) => (
                        <Nav.Item key={index}>
                          <Nav.Link 
                            active={selectedActivityIndex === index}
                            onClick={() => handleSelectActivity(index)}
                          >
                            {t('planDetail.aiActivity.option')} {index + 1}
                          </Nav.Link>
                        </Nav.Item>
                      ))}
                    </Nav>
                    
                    <Card className="mb-3">
                      <Card.Body>
                        <h5 className="mb-2">{generatedActivity?.name}</h5>
                        <Badge bg="primary" className="mb-2">{generatedActivity?.type}</Badge>
                        {generatedActivity?.description && (
                          <p className="mb-2">{generatedActivity.description}</p>
                        )}
                        {/* Render images for the selected AI-generated activity */}
                        {generatedActivity?.images && generatedActivity.images.length > 0 && (
                          <div className="d-flex gap-2 mt-2">
                            {generatedActivity.images.map((image, imgIndex) => (
                              image.url && (
                                <div key={imgIndex} className="position-relative">
                                  <img 
                                    src={image.url} 
                                    alt={`${generatedActivity.name} - view ${imgIndex+1}`}
                                    className="img-thumbnail"
                                    style={{ 
                                      width: '100px', 
                                      height: '75px',
                                      objectFit: 'cover'
                                    }}
                                  />
                                  {/* Attribution info */}
                                  {image.attribution && (
                                    <div className="text-muted small text-center">
                                      <small title={`${image.attribution.name}${image.provider ? ` (${image.provider})` : ''}`}>
                                        by: {image.attribution.name.length > 20 
                                          ? image.attribution.name.substring(0, 20) + '...' 
                                          : image.attribution.name}
                                      </small>
                                    </div>
                                  )}
                                </div>
                              )
                            ))}
                          </div>
                        )}
                        <div className="small text-muted mb-2">
                          {generatedActivity?.location && (
                            <div className="mb-1">
                              <i className="bi bi-geo-alt me-1"></i>
                              <strong>Location:</strong> {normalizeLocation(generatedActivity.location).address}
                            </div>
                          )}
                          {generatedActivity?.estimatedDuration && (
                            <div className="mb-1">
                              <i className="bi bi-clock me-1"></i>
                              <strong>Duration:</strong> {generatedActivity.estimatedDuration}
                            </div>
                          )}
                          {generatedActivity?.price && (
                            <div className="mb-1">
                              <i className="bi bi-tag me-1"></i>
                              <strong>Price:</strong> {generatedActivity.price}
                            </div>
                          )}
                          {generatedActivity?.notes && (
                            <div className="mb-1">
                              <i className="bi bi-journal-text me-1"></i>
                              <strong>Notes:</strong> {generatedActivity.notes}
                            </div>
                          )}
                        </div>
                      </Card.Body>
                    </Card>
                    
                    <div className="d-flex justify-content-end mt-3">
                      <Button 
                        variant="outline-secondary" 
                        className="me-2"
                        onClick={() => {
                          setGeneratedActivities([]);
                          setGeneratedActivity(null);
                        }}
                      >
                        {t('planDetail.aiActivity.tryAgain')}
                      </Button>
                      <Button 
                        variant="primary" 
                        onClick={handleAddGeneratedActivity}
                      >
                        {t('planDetail.aiActivity.addToPlan')}
                      </Button>
                    </div>
                  </div>
                )}
              </Modal.Body>
            </Modal>
            
          </>
        ) : (
          <Alert variant="warning">
            Failed to load plan data. Please try again.
          </Alert>
        )}
      </Container>
      
      <VersionFooter />      
      
    </div>
  );
}